import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import "./Resume.css";

/* ═══════════════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════════════ */
const TEMPLATES = [
  { id: "modern",    name: "Modern Pro",        type: "ats",    tag: "ATS",    accent: "#2563eb", preview: "M", desc: "Clean sidebar, highest ATS score" },
  { id: "minimal",   name: "Minimal Edge",      type: "ats",    tag: "ATS",    accent: "#111827", preview: "E", desc: "Ultra-clean scannable layout" },
  { id: "corporate", name: "Corporate Classic", type: "ats",    tag: "ATS",    accent: "#1e3a5f", preview: "C", desc: "Traditional two-section ATS-safe" },
  { id: "creative",  name: "Creative Spark",    type: "design", tag: "Design", accent: "#7c3aed", preview: "D", desc: "Sidebar layout with colour accents" },
  { id: "gradient",  name: "Gradient Bold",     type: "design", tag: "Design", accent: "#db2777", preview: "G", desc: "Bold header with gradient sidebar" },
  { id: "elegant",   name: "Elegant Dark",      type: "design", tag: "Design", accent: "#0f172a", preview: "L", desc: "Dark header, luxury feel" },
];

const THEME_GROUPS = [
  { label: "Professional", themes: [
    { id: "royal",    label: "Royal Blue",  primary: "#2563eb" },
    { id: "navy",     label: "Navy",        primary: "#1e3a5f" },
    { id: "slate",    label: "Slate",       primary: "#334155" },
    { id: "charcoal", label: "Charcoal",    primary: "#1c1c1e" },
    { id: "steel",    label: "Steel Blue",  primary: "#1e40af" },
  ]},
  { label: "Bold", themes: [
    { id: "violet",   label: "Violet",      primary: "#7c3aed" },
    { id: "plum",     label: "Deep Plum",   primary: "#581c87" },
    { id: "rose",     label: "Rose",        primary: "#be123c" },
    { id: "pink",     label: "Hot Pink",    primary: "#db2777" },
    { id: "burgundy", label: "Burgundy",    primary: "#7f1d1d" },
  ]},
  { label: "Nature", themes: [
    { id: "teal",     label: "Teal",        primary: "#0d7377" },
    { id: "emerald",  label: "Emerald",     primary: "#394420" },
    { id: "forest",   label: "Forest",      primary: "#166534" },
    { id: "olive",    label: "Olive",       primary: "#4d7c0f" },
    { id: "cyan",     label: "Cyan",        primary: "#0e7490" },
  ]},
  { label: "Modern", themes: [
    { id: "indigo",   label: "Indigo",      primary: "#4338ca" },
    { id: "amber",    label: "Amber",       primary: "#b45309" },
    { id: "orange",   label: "Orange",      primary: "#c2410c" },
    { id: "midnight", label: "Midnight",    primary: "#0f172a" },
    { id: "sienna",   label: "Sienna",      primary: "#92400e" },
  ]},
];

const ALL_THEMES = THEME_GROUPS.flatMap(g => g.themes);

const BODY_COLORS = [
  { label: "White",      bg: "#ffffff", text: "#1a1a1a" },
  { label: "Warm White", bg: "#fdf9f5", text: "#1a1a1a" },
  { label: "Light Gray", bg: "#f8fafc", text: "#1a1a1a" },
  { label: "Cream",      bg: "#fffbeb", text: "#1a1a1a" },
  { label: "Light Blue", bg: "#eff6ff", text: "#1a1a1a" },
  { label: "Light Green",bg: "#f0fdf4", text: "#1a1a1a" },
];

const QUICK_COLORS = [
  { color: "#2563eb", label: "Blue" },
  { color: "#7c3aed", label: "Violet" },
  { color: "#394420", label: "Green" },
  { color: "#dc2626", label: "Red" },
  { color: "#0f172a", label: "Dark" },
];

const EMPTY_DATA = {
  name: "", title: "", email: "", phone: "", location: "",
  linkedin: "", website: "", photo: null, summary: "",
  experience: [], education: [], skills: [], certifications: [], languages: [], projects: [],
};

/* ── Voice Language Config ── */
const VOICE_LANGUAGES = [
  { id: "english",  label: "English",  lang: "en-IN",  flag: "🇬🇧" },
  { id: "hindi",    label: "Hindi",    lang: "hi-IN",  flag: "🇮🇳" },
  { id: "hinglish", label: "Hinglish", lang: "en-IN",  flag: "🔀" },
];

/* ── Steps with multi-language questions ── */
const STEPS = [
  {
    key: "name",
    q:  { english: "What is your full name?", hindi: "आपका पूरा नाम क्या है?", hinglish: "Aapka full name kya hai?" },
    hint: { english: "e.g. Priya Sharma", hindi: "जैसे: प्रिया शर्मा", hinglish: "e.g. Priya Sharma" },
  },
  {
    key: "title",
    q:  { english: "What is your professional title or target role?", hindi: "आपका पेशेवर पद या लक्ष्य भूमिका क्या है?", hinglish: "Aapka professional title ya target role kya hai?" },
    hint: { english: "e.g. Software Engineer", hindi: "जैसे: सॉफ्टवेयर इंजीनियर", hinglish: "e.g. Software Engineer" },
  },
  {
    key: "email",
    q:  { english: "What is your email address?", hindi: "आपका ईमेल पता क्या है?", hinglish: "Aapka email address kya hai?" },
    hint: { english: "e.g. priya@email.com", hindi: "जैसे: priya@email.com", hinglish: "e.g. priya@email.com" },
  },
  {
    key: "phone",
    q:  { english: "What is your phone number?", hindi: "आपका फ़ोन नंबर क्या है?", hinglish: "Aapka phone number kya hai?" },
    hint: { english: "e.g. +91 98765 43210", hindi: "जैसे: +91 98765 43210", hinglish: "e.g. +91 98765 43210" },
  },
  {
    key: "location",
    q:  { english: "What is your current city and state?", hindi: "आपका वर्तमान शहर और राज्य क्या है?", hinglish: "Aapka current city aur state kya hai?" },
    hint: { english: "e.g. Bangalore, Karnataka", hindi: "जैसे: बेंगलुरु, कर्नाटक", hinglish: "e.g. Bangalore, Karnataka" },
  },
  {
    key: "linkedin",
    q:  { english: "LinkedIn profile URL? Say skip to skip.", hindi: "LinkedIn प्रोफाइल URL? छोड़ने के लिए 'skip' कहें।", hinglish: "LinkedIn profile URL? Skip karna ho toh 'skip' bolo." },
    hint: { english: "e.g. linkedin.com/in/priya", hindi: "जैसे: linkedin.com/in/priya", hinglish: "e.g. linkedin.com/in/priya" },
  },
  {
    key: "summary",
    q:  { english: "Tell me about yourself in 2-3 sentences. What makes you a great candidate?", hindi: "2-3 वाक्यों में अपने बारे में बताएं। आपको एक अच्छा उम्मीदवार क्या बनाता है?", hinglish: "2-3 sentences mein apne baare mein batao. Aap ek accha candidate kyu hain?" },
    hint: { english: "Your professional summary", hindi: "आपका पेशेवर सारांश", hinglish: "Aapka professional summary" },
  },
  {
    key: "experience",
    q:  { english: "Tell me about your work experience — company, role, duration, and achievements.", hindi: "अपने कार्य अनुभव के बारे में बताएं — कंपनी, पद, अवधि और उपलब्धियां।", hinglish: "Apna work experience batao — company, role, duration aur achievements." },
    hint: { english: "e.g. TCS as Developer for 2 years, built payment module", hindi: "जैसे: TCS में डेवलपर 2 साल, पेमेंट मॉड्यूल बनाया", hinglish: "e.g. TCS mein Developer 2 saal, payment module banaya" },
  },
  {
    key: "education",
    q:  { english: "What is your educational background? Include degree, college, and year.", hindi: "आपकी शैक्षिक पृष्ठभूमि क्या है? डिग्री, कॉलेज और वर्ष बताएं।", hinglish: "Aapki education kya hai? Degree, college aur year batao." },
    hint: { english: "e.g. B.Tech from IIT 2020", hindi: "जैसे: IIT से B.Tech 2020", hinglish: "e.g. IIT se B.Tech 2020" },
  },
  {
    key: "skills",
    q:  { english: "List your top skills separated by commas.", hindi: "अपने शीर्ष कौशल अल्पविराम से अलग करके बताएं।", hinglish: "Apne top skills comma se alag karke batao." },
    hint: { english: "e.g. React, Node.js, Python, SQL", hindi: "जैसे: React, Node.js, Python, SQL", hinglish: "e.g. React, Node.js, Python, SQL" },
  },
  {
    key: "certifications",
    q:  { english: "Any certifications? Say skip if none.", hindi: "कोई प्रमाणपत्र? न होने पर 'skip' कहें।", hinglish: "Koi certifications hain? Nahi toh 'skip' bolo." },
    hint: { english: "e.g. AWS Certified Developer", hindi: "जैसे: AWS Certified Developer", hinglish: "e.g. AWS Certified Developer" },
  },
  {
    key: "projects",
    q:  { english: "Describe a key project. Say skip if none.", hindi: "किसी प्रमुख प्रोजेक्ट का वर्णन करें। न होने पर 'skip' कहें।", hinglish: "Ek important project describe karo. Nahi toh 'skip' bolo." },
    hint: { english: "e.g. Built an e-commerce site with React", hindi: "जैसे: React से ई-कॉमर्स साइट बनाई", hinglish: "e.g. React se e-commerce site banayi" },
  },
];

/* Rich sample data */
const PREVIEW_SAMPLE = {
  name: "Priya Sharma", title: "Senior Software Engineer",
  email: "priya.sharma@email.com", phone: "+91 98765 43210",
  location: "Bangalore, Karnataka", linkedin: "linkedin.com/in/priya-sharma",
  summary: "Results-driven Software Engineer with 6+ years building scalable web applications. Led cross-functional teams of 8+ engineers at top MNCs. Expertise in React, Node.js, and cloud architecture.",
  experience: [
    { id: "1", company: "Google India", role: "Senior Software Engineer", duration: "2021 – Present",
      bullets: ["Led payment module handling ₹50Cr+ monthly transactions, improving throughput by 35%", "Reduced API response time by 40% through query optimisation and caching"] },
    { id: "2", company: "Infosys", role: "Software Engineer", duration: "2018 – 2021",
      bullets: ["Built microservices architecture serving 2M+ daily active users", "Improved CI/CD pipeline reducing deployment time by 60%"] },
  ],
  education: [{ id: "1", degree: "B.Tech Computer Science", institution: "IIT Bombay", year: "2018", gpa: "8.7 CGPA" }],
  skills: ["React", "Node.js", "Python", "TypeScript", "AWS", "Docker", "PostgreSQL", "Git", "GraphQL", "Redis"],
  certifications: [
    { id: "1", name: "AWS Certified Developer – Associate", issuer: "Amazon", year: "2022" },
    { id: "2", name: "Google Cloud Professional", issuer: "Google", year: "2023" },
  ],
  projects: [{ id: "1", name: "E-Commerce Platform", description: "Built full-stack app with 10K+ daily active users using React and Node.js.", tech: "React, Node.js, MongoDB", link: "github.com/priya/ecom" }],
  languages: [], photo: null,
};

/* ── Validators ── */
const VALIDATORS = {
  name:     v => { if (!v||v.trim().length<2) return "Full name must be at least 2 characters"; if (v.trim().length>80) return "Name too long (max 80 chars)"; if (!/^[a-zA-Z\s\u0900-\u097F'-]+$/.test(v.trim())) return "Name should only contain letters"; return null; },
  title:    v => { if (!v||v.trim().length<2) return "Professional title is required (min 2 chars)"; if (v.trim().length>100) return "Title too long"; return null; },
  email:    v => { if (!v||!v.trim()) return "Email address is required"; if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) return "Enter a valid email (e.g. name@example.com)"; return null; },
  phone:    v => { if (!v||!v.trim()) return "Phone number is required"; const c=v.replace(/[\s\-().+]/g,""); if (!/^\d{7,15}$/.test(c)) return "Enter a valid phone number (7–15 digits)"; return null; },
  location: v => { if (v&&v.trim().length>100) return "Location too long"; return null; },
  linkedin: v => { if (!v||/skip|none|nahi|nahin/i.test(v)) return null; return null; },
  summary:  v => { if (!v||v.trim().length<30) return "Summary should be at least 30 characters for better ATS score"; if (v.trim().length>600) return "Summary too long (max 600 chars recommended)"; return null; },
  experience: v => { if (!v||v.trim().length<5) return "Please describe your work experience"; return null; },
  education: v => { if (!v||v.trim().length<5) return "Please describe your educational background"; return null; },
  skills:   v => { if (!v||v.trim().length<2) return "Please list at least one skill"; return null; },
};
function validateField(key, value) { const fn = VALIDATORS[key]; return fn ? fn(value) : null; }

/* ── Helpers ── */
function uid() { return Math.random().toString(36).slice(2, 9); }
function parseExperience(t) {
  const l = t.split(/[.,;\n]/).map(s=>s.trim()).filter(s=>s.length>3);
  if (!l.length) return [{ id:uid(), company:t, role:"", duration:"", bullets:[] }];
  return [{ id:uid(), company:l[0]||"", role:l[1]||"", duration:l[2]||"", bullets:l.slice(3).filter(Boolean) }];
}
function parseEducation(t) { const p=t.split(/[,;-]/).map(s=>s.trim()); return [{ id:uid(), degree:p[0]||t, institution:p[1]||"", year:p[2]||"", gpa:"" }]; }
function parseSkills(t) { return t.split(/[,;/\s]+/).map(s=>s.trim()).filter(s=>s.length>1); }
function parseCertifications(t) { if (!t||/skip|none|nahi|nahin/i.test(t)) return []; return t.split(/[,;]/).map(c=>({ id:uid(), name:c.trim(), issuer:"", year:"" })).filter(c=>c.name); }
function parseProjects(t) { if (!t||/skip|none|nahi|nahin/i.test(t)) return []; return [{ id:uid(), name:"", description:t, tech:"", link:"" }]; }

function calcATS(d) {
  let s=0; const r=[];
  if (d.name?.trim()) s+=10; else r.push("Add your full name");
  if (d.email?.trim()) s+=10; else r.push("Add email address");
  if (d.phone?.trim()) s+=8; else r.push("Add phone number");
  if (d.location?.trim()) s+=5; else r.push("Add location");
  if ((d.summary?.length||0)>50) s+=15; else r.push("Add professional summary (50+ chars)");
  if (d.experience?.length>0) s+=20; else r.push("Add work experience");
  if (d.education?.length>0) s+=12; else r.push("Add education");
  if ((d.skills?.length||0)>=5) s+=10; else r.push("Add at least 5 skills");
  if ((d.skills?.length||0)>=10) s+=5;
  if (d.certifications?.length>0) s+=5;
  return { score: Math.min(s,100), reasons: r };
}

/* ── Gemini API ── */
const GEMINI_MODEL = "gemini-1.5-flash";
async function callGemini(prompt, maxTokens=2500) {
  const key = process.env.REACT_APP_GEMINI_API_KEY;
  if (!key) { console.error("REACT_APP_GEMINI_API_KEY not set"); return ""; }
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent?key=${key}`;
  const body = JSON.stringify({ contents:[{ parts:[{ text:prompt }] }], generationConfig:{ maxOutputTokens:maxTokens, temperature:0.3 } });
  const delays = [5000,15000,30000];
  for (let attempt=0; attempt<3; attempt++) {
    try {
      const res = await fetch(url, { method:"POST", headers:{ "Content-Type":"application/json" }, body });
      if (res.status===429) { if (attempt<2) { await new Promise(r=>setTimeout(r,delays[attempt])); continue; } return ""; }
      if (!res.ok) return "";
      const json = await res.json();
      return json?.candidates?.[0]?.content?.parts?.[0]?.text?.trim()||"";
    } catch(e) { if (attempt===2) return ""; }
  }
  return "";
}

async function cleanFieldWithAI(rawText, fieldKey) {
  const noTouch = ["email","phone","linkedin","website"];
  if (noTouch.includes(fieldKey)) return { cleaned:rawText, changed:false };
  const prompt = `You are a professional resume writer and language expert. The user may have spoken in Hindi, English, or Hinglish (mix). 
Field: "${fieldKey}"
Tasks:
1. Translate any Hindi/Hinglish to professional English
2. Fix ALL spelling, grammar mistakes  
3. Remove filler words (um, uh, ahem, basically, actually, toh, matlab, etc.)
4. If field is name/title/location: Keep as-is but fix spelling only
5. If summary/experience: Rewrite as powerful professional resume content with action verbs and metrics
6. If skills: Return clean comma-separated skill names in English
7. If input means skip/none/nahi/nahin: return exactly: SKIP
8. Return ONLY the corrected English text, nothing else.

Raw input: "${rawText}"`;
  try {
    const result = await callGemini(prompt, 600);
    const cleaned = result?.trim()||rawText;
    if (cleaned.toUpperCase()==="SKIP") return { cleaned:"skip", changed:false };
    return { cleaned, changed: cleaned.toLowerCase().trim()!==rawText.toLowerCase().trim() };
  } catch { return { cleaned:rawText, changed:false }; }
}

async function getAISuggestion(fieldKey, currentValue) {
  if (!currentValue || currentValue.trim().length < 3) return null;
  const prompts = {
    summary: `Improve this resume summary to be more impactful (max 3 sentences): "${currentValue}"`,
    experience: `Make this work experience description more professional with action verbs and quantifiable achievements: "${currentValue}"`,
    skills: `Given these skills, suggest 3-5 additional relevant skills as a comma-separated list: "${currentValue}". Return ONLY the additional skills.`,
    title: `Suggest a more professional job title variation for: "${currentValue}". Return only the title.`,
  };
  const p = prompts[fieldKey];
  if (!p) return null;
  try {
    const result = await callGemini(p, 300);
    return result?.trim()||null;
  } catch { return null; }
}

async function parseResumeWithGemini(text) {
  const prompt = `You are a resume parser. Extract and return ONLY a raw JSON object.\nCRITICAL: Return ONLY JSON, start with { end with }.\n\n{\n  "name":"","title":"","email":"","phone":"","location":"","linkedin":"","website":"","summary":"",\n  "experience":[{"company":"","role":"","duration":"","bullets":[]}],\n  "education":[{"degree":"","institution":"","year":"","gpa":""}],\n  "skills":[],"certifications":[{"name":"","issuer":"","year":""}],\n  "projects":[{"name":"","description":"","tech":"","link":""}],"languages":[]\n}\n\nResume text:\n${text.slice(0,6000)}`;
  try {
    const raw = await callGemini(prompt, 2500);
    if (!raw||raw.trim().length===0) return null;
    const firstBrace = raw.indexOf("{");
    if (firstBrace===-1) return null;
    let depth=0, endIndex=-1;
    for (let i=firstBrace; i<raw.length; i++) {
      if (raw[i]==="{") depth++; if (raw[i]==="}") depth--;
      if (depth===0) { endIndex=i; break; }
    }
    if (endIndex===-1) return null;
    const parsed = JSON.parse(raw.slice(firstBrace, endIndex+1));
    parsed.experience     = (parsed.experience||[]).map(e=>({ ...e, id:uid() }));
    parsed.education      = (parsed.education||[]).map(e=>({ ...e, id:uid() }));
    parsed.certifications = (parsed.certifications||[]).map(c=>({ ...c, id:uid() }));
    parsed.projects       = (parsed.projects||[]).map(p=>({ ...p, id:uid() }));
    return { ...EMPTY_DATA, ...parsed };
  } catch(e) { console.error("Parse error:", e); return null; }
}

const SERVER_URL = (process.env.REACT_APP_API_URL||"http://localhost:5000").replace(/\/$/,"").replace(/\/api$/,"");

/* ═══════════════════════════════════════════════════════════
   VOICE ENGINE HOOK
═══════════════════════════════════════════════════════════ */
function useVoiceEngine({ language, voiceGender, onResult, onError }) {
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceSupported] = useState(()=>{
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  });
  const [ttsSupported] = useState(()=> !!window.speechSynthesis);
  const [availableVoices, setAvailableVoices] = useState([]);

  useEffect(()=>{
    function loadVoices() {
      const voices = synthRef.current?.getVoices()||[];
      setAvailableVoices(voices);
    }
    loadVoices();
    if (synthRef.current) {
      synthRef.current.onvoiceschanged = loadVoices;
    }
  },[]);

  const getLangCode = useCallback(()=>{
    const lang = VOICE_LANGUAGES.find(l=>l.id===language);
    return lang?.lang || "en-IN";
  },[language]);

  const getBestVoice = useCallback(()=>{
    if (!availableVoices.length) return null;
    const langCode = getLangCode();
    const genderKeywords = voiceGender==="female"
      ? ["female","woman","zira","hazel","susan","veena","lekha","samantha","victoria"]
      : ["male","man","david","mark","rajan","daniel","james"];
    
    // Try to find voice matching language and gender
    let voice = availableVoices.find(v=>
      v.lang.startsWith(langCode.split("-")[0]) &&
      genderKeywords.some(k=>v.name.toLowerCase().includes(k))
    );
    // Fallback: any voice matching language
    if (!voice) voice = availableVoices.find(v=>v.lang.startsWith(langCode.split("-")[0]));
    // Fallback: any voice matching gender keywords
    if (!voice) voice = availableVoices.find(v=>genderKeywords.some(k=>v.name.toLowerCase().includes(k)));
    // Final fallback: first available
    if (!voice && availableVoices.length) voice = availableVoices[0];
    return voice;
  },[availableVoices, getLangCode, voiceGender]);

  const speak = useCallback((text, onDone)=>{
    if (!ttsSupported || !text) { onDone?.(); return; }
    synthRef.current.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    const voice = getBestVoice();
    if (voice) utterance.voice = voice;
    utterance.lang = getLangCode();
    utterance.rate = 0.92;
    utterance.pitch = voiceGender==="female" ? 1.15 : 0.88;
    utterance.volume = 1;
    utterance.onstart = ()=>setIsSpeaking(true);
    utterance.onend = ()=>{ setIsSpeaking(false); onDone?.(); };
    utterance.onerror = ()=>{ setIsSpeaking(false); onDone?.(); };
    synthRef.current.speak(utterance);
  },[ttsSupported, getBestVoice, getLangCode, voiceGender]);

  const stopSpeaking = useCallback(()=>{
    synthRef.current?.cancel();
    setIsSpeaking(false);
  },[]);

  const startListening = useCallback(()=>{
    if (!voiceSupported) { onError?.("Voice input not supported in this browser. Please use Chrome."); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    recognitionRef.current = rec;
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = getLangCode();
    rec.maxAlternatives = 1;
    rec.onstart = ()=>setIsListening(true);
    rec.onend = ()=>setIsListening(false);
    rec.onerror = (e)=>{
      setIsListening(false);
      if (e.error==="no-speech") onError?.("No speech detected. Please try again.");
      else if (e.error==="not-allowed") onError?.("Microphone access denied. Please allow microphone permission.");
      else onError?.(`Voice error: ${e.error}`);
    };
    rec.onresult = (e)=>{
      const transcript = Array.from(e.results).map(r=>r[0].transcript).join(" ");
      if (e.results[e.results.length-1].isFinal) {
        onResult?.(transcript.trim());
        setIsListening(false);
      }
    };
    try { rec.start(); } catch(e) { setIsListening(false); onError?.("Failed to start voice recognition."); }
  },[voiceSupported, getLangCode, onResult, onError]);

  const stopListening = useCallback(()=>{
    recognitionRef.current?.stop();
    setIsListening(false);
  },[]);

  return { isListening, isSpeaking, voiceSupported, ttsSupported, speak, stopSpeaking, startListening, stopListening };
}

/* ═══════════════════════════════════════════════════════════
   COLOUR BAR
═══════════════════════════════════════════════════════════ */
function ColorBar({ accent, onAccentChange, bodyColor, onBodyColorChange, showBody=false }) {
  const [showPicker, setShowPicker] = useState(false);
  const [customVal, setCustomVal]   = useState(accent);
  return (
    <div style={{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:12, padding:"12px 16px", marginBottom:14 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom: showBody ? 10 : 0 }}>
        <span style={{ fontSize:"0.72rem", fontWeight:700, color:"#475569", whiteSpace:"nowrap", minWidth:90 }}>Accent Colour:</span>
        <div style={{ display:"flex", gap:7, alignItems:"center", flex:1 }}>
          {QUICK_COLORS.map(({ color, label }) => (
            <button key={color} title={label} onClick={() => { onAccentChange(color); setCustomVal(color); setShowPicker(false); }}
              style={{ width:24, height:24, borderRadius:"50%", background:color, padding:0, flexShrink:0, border: accent===color ? "3px solid #0f172a" : "2px solid rgba(0,0,0,0.08)", cursor:"pointer", boxShadow:"0 2px 6px rgba(0,0,0,0.18)", transform: accent===color ? "scale(1.25)" : "scale(1)", transition:"all 0.15s" }}
            />
          ))}
          <div style={{ position:"relative" }}>
            <button title="Custom colour" onClick={() => setShowPicker(v=>!v)}
              style={{ width:24, height:24, borderRadius:"50%", padding:0, flexShrink:0, background:"conic-gradient(#e11d48,#f97316,#eab308,#263012,#0ea5e9,#7c3aed,#e11d48)", border: showPicker ? "3px solid #0f172a" : "2px solid rgba(0,0,0,0.12)", cursor:"pointer", boxShadow:"0 2px 6px rgba(0,0,0,0.2)" }}
            />
            {showPicker && (
              <div style={{ position:"absolute", top:32, left:0, zIndex:200, background:"#fff", border:"1px solid #e2e8f0", borderRadius:12, padding:12, boxShadow:"0 8px 24px rgba(0,0,0,0.14)", display:"flex", flexDirection:"column", gap:8, minWidth:170 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <input type="color" value={customVal} onChange={e => { setCustomVal(e.target.value); onAccentChange(e.target.value); }} style={{ width:38, height:34, border:"none", borderRadius:6, cursor:"pointer", padding:2 }}/>
                  <input type="text" value={customVal} maxLength={7} placeholder="#000000" onChange={e => { setCustomVal(e.target.value); if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) onAccentChange(e.target.value); }} style={{ flex:1, border:"1px solid #e2e8f0", borderRadius:6, padding:"5px 8px", fontSize:"0.78rem", fontFamily:"monospace", outline:"none" }}/>
                </div>
                <button onClick={() => { onAccentChange(customVal); setShowPicker(false); }} style={{ background:customVal, color:"#fff", border:"none", borderRadius:7, padding:"8px", fontSize:"0.75rem", fontWeight:700, cursor:"pointer" }}>Apply Colour</button>
              </div>
            )}
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:5, marginLeft:"auto" }}>
          <div style={{ width:14, height:14, borderRadius:3, background:accent, boxShadow:"0 1px 3px rgba(0,0,0,0.2)" }}/>
          <span style={{ fontFamily:"monospace", fontSize:"0.68rem", color:"#94a3b8" }}>{accent}</span>
        </div>
      </div>
      {showBody && onBodyColorChange && (
        <div style={{ display:"flex", alignItems:"center", gap:10, paddingTop:10, borderTop:"1px solid #e2e8f0" }}>
          <span style={{ fontSize:"0.72rem", fontWeight:700, color:"#475569", whiteSpace:"nowrap", minWidth:90 }}>Background:</span>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {BODY_COLORS.map(({ label, bg }) => (
              <button key={bg} title={label} onClick={() => onBodyColorChange(bg)}
                style={{ width:22, height:22, borderRadius:5, background:bg, padding:0, border: bodyColor===bg ? "2.5px solid #0f172a" : "1.5px solid #cbd5e1", cursor:"pointer", boxShadow:"0 1px 3px rgba(0,0,0,0.12)", transform: bodyColor===bg ? "scale(1.2)" : "scale(1)", transition:"all 0.12s" }}
              />
            ))}
            <input type="color" value={bodyColor||"#ffffff"} title="Custom background" onChange={e => onBodyColorChange(e.target.value)} style={{ width:22, height:22, border:"1.5px solid #cbd5e1", borderRadius:5, padding:1, cursor:"pointer", background:"none" }}/>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   UPLOAD RESUME PANEL
═══════════════════════════════════════════════════════════ */
function UploadResumePanel({ onParsed, onDismiss }) {
  const [dragOver, setDragOver]   = useState(false);
  const [parsing, setParsing]     = useState(false);
  const [progress, setProgress]   = useState(0);
  const [statusMsg, setStatusMsg] = useState("");
  const [error, setError]         = useState("");
  const [fileName, setFileName]   = useState("");
  const fileRef = useRef(null);
  const messages = ["Reading your resume file...","Extracting text...","Sending to AI...","Identifying skills...","Building profile...","Almost done..."];

  async function processFile(f) {
    if (f.size>3*1024*1024) { setError("File too large (max 3MB)."); return; }
    setFileName(f.name); setError(""); setParsing(true); setProgress(0);
    let step=0; setStatusMsg(messages[0]);
    const iv = setInterval(() => { step=Math.min(step+1,messages.length-1); setStatusMsg(messages[step]); setProgress(Math.min(80,(step+1)*13)); }, 900);
    try {
      const ext = f.name.split(".").pop().toLowerCase();
      if (!["pdf","doc","docx","txt"].includes(ext)) throw new Error("Only PDF, DOC, DOCX, or TXT files allowed.");
      let resumeText = null;
      if (ext==="txt") { resumeText = await f.text(); }
      else {
        const base64 = await new Promise((res,rej) => { const r=new FileReader(); r.onload=()=>res(r.result.split(",")[1]); r.onerror=()=>rej(new Error("Could not read file.")); r.readAsDataURL(f); });
        const resp = await fetch(`${SERVER_URL}/api/resume/extract-text`, { method:"POST", headers:{ "Content-Type":"application/json" }, body:JSON.stringify({ fileName:f.name, fileData:base64 }) });
        if (resp.ok) { const json=await resp.json(); if (json.success&&json.text) resumeText=json.text; else throw new Error(json.error||"Extraction failed"); }
        else throw new Error(`Server returned ${resp.status}`);
      }
      if (!resumeText||resumeText.trim().length<30) throw new Error("Could not extract enough text.");
      setStatusMsg("Parsing with AI..."); setProgress(85);
      const parsed = await parseResumeWithGemini(resumeText);
      clearInterval(iv); setProgress(100); setStatusMsg("✓ Resume parsed!");
      if (!parsed) { setError("AI quota reached. Try again in a few minutes."); setParsing(false); return; }
      setTimeout(() => { setParsing(false); onParsed(parsed); }, 700);
    } catch(err) { clearInterval(iv); setError(err.message||"Failed to process file."); setParsing(false); }
  }

  return (
    <div className="th-overlay" onClick={e=>e.target===e.currentTarget&&!parsing&&onDismiss()}>
      <div className="th-modal upload-modal">
        <div className="th-modal-header">
          <div><div className="upload-title">📄 Upload Your Resume</div><div className="upload-subtitle">AI extracts all your info automatically</div></div>
          {!parsing && <button className="th-close-btn" onClick={onDismiss}>✕</button>}
        </div>
        {!parsing ? (
          <div style={{ padding:"0 24px 24px" }}>
            <div className={`upload-dropzone ${dragOver?"upload-dropzone--over":"upload-dropzone--idle"}`}
              onDragOver={e=>{ e.preventDefault(); setDragOver(true); }} onDragLeave={()=>setDragOver(false)}
              onDrop={e=>{ e.preventDefault(); setDragOver(false); const f=e.dataTransfer.files[0]; if(f) processFile(f); }}
              onClick={()=>fileRef.current?.click()}>
              <div className="upload-dz-icon">{dragOver?"📂":"📋"}</div>
              <div className="upload-dz-title">{dragOver?"Drop it here!":"Drag & drop your resume"}</div>
              <div className="upload-dz-sub">PDF, DOC, DOCX, or TXT</div>
              <button className="th-btn th-btn--primary" onClick={e=>{e.stopPropagation();fileRef.current?.click();}}>Browse Files</button>
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.txt" hidden onChange={e=>e.target.files[0]&&processFile(e.target.files[0])}/>
            </div>
            {error && <div className="upload-error">⚠️ {error}</div>}
            <div className="upload-formats">{["PDF","DOCX","DOC","TXT"].map(f=><span key={f} className="upload-fmt-tag">{f}</span>)}</div>
          </div>
        ) : (
          <div style={{ padding:"12px 24px 28px" }}>
            <div className="upload-progress-wrap">
              <div className={`upload-ai-icon ${progress<100?"upload-ai-icon--pulsing":"upload-ai-icon--done"}`}>{progress<100?"🤖":"✅"}</div>
              <div className="upload-status-msg">{statusMsg}</div>
              <div className="upload-filename">{fileName}</div>
              <div className="upload-bar-track"><div className="upload-bar-fill" style={{ width:`${progress}%` }}/></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PHOTO EDITOR
═══════════════════════════════════════════════════════════ */
function PhotoEditor({ src, onSave, onClose }) {
  const canvasRef = useRef(null);
  const [zoom,setZoom] = useState(1);
  const [ox,setOx] = useState(0);
  const [oy,setOy] = useState(0);
  const [drag,setDrag] = useState(false);
  const [sp,setSp] = useState({ x:0, y:0 });
  const [bright,setBright] = useState(100);
  const [cont,setCont] = useState(100);
  const imgRef = useRef(new window.Image());
  useEffect(() => { imgRef.current.onload=()=>draw(); imgRef.current.src=src; }, [src]); // eslint-disable-line
  useEffect(() => { draw(); }, [zoom,ox,oy,bright,cont]); // eslint-disable-line
  function draw() {
    const c=canvasRef.current; if(!c||!imgRef.current.complete) return;
    const ctx=c.getContext("2d"); const sz=240;
    c.width=sz; c.height=sz; ctx.filter=`brightness(${bright}%) contrast(${cont}%)`;
    ctx.clearRect(0,0,sz,sz); ctx.beginPath(); ctx.arc(sz/2,sz/2,sz/2,0,Math.PI*2); ctx.clip();
    const img=imgRef.current; const sc=Math.min(sz/img.width,sz/img.height)*zoom;
    ctx.drawImage(img,(sz-img.width*sc)/2+ox,(sz-img.height*sc)/2+oy,img.width*sc,img.height*sc);
  }
  return (
    <div className="th-overlay">
      <div className="th-modal photo-modal">
        <div className="th-modal-header"><span className="th-modal-title">Edit Profile Photo</span><button className="th-close-btn" onClick={onClose}>✕</button></div>
        <div style={{ padding:"0 24px 24px" }}>
          <div className="photo-canvas-wrap">
            <canvas ref={canvasRef} onMouseDown={e=>{ setDrag(true); setSp({ x:e.clientX-ox, y:e.clientY-oy }); }} onMouseMove={e=>{ if(drag){ setOx(e.clientX-sp.x); setOy(e.clientY-sp.y); } }} onMouseUp={()=>setDrag(false)} onMouseLeave={()=>setDrag(false)}/>
          </div>
          <div className="photo-controls">
            {[
              { label:"Zoom", val:zoom, set:setZoom, min:0.5, max:3, step:0.05, fmt:v=>Math.round(v*100)+"%" },
              { label:"Brightness", val:bright, set:setBright, min:50, max:150, step:1, fmt:v=>v+"%" },
              { label:"Contrast", val:cont, set:setCont, min:50, max:150, step:1, fmt:v=>v+"%" },
            ].map(c=>(
              <div key={c.label} className="photo-ctrl-row">
                <label>{c.label}</label>
                <input type="range" min={c.min} max={c.max} step={c.step} value={c.val} onChange={e=>c.set(+e.target.value)}/>
                <span>{c.fmt(c.val)}</span>
              </div>
            ))}
          </div>
          <div className="photo-actions">
            <button className="th-btn th-btn--ghost" onClick={()=>{ setZoom(1.4); setOx(0); setOy(0); setBright(105); setCont(110); }}>Passport Style</button>
            <button className="th-btn th-btn--primary" onClick={()=>{ const c=canvasRef.current; const s=document.createElement("canvas"); s.width=s.height=240; s.getContext("2d").drawImage(c,0,0); onSave(s.toDataURL("image/jpeg",0.92)); }}>Use Photo ✓</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   THEME PICKER (sidebar)
═══════════════════════════════════════════════════════════ */
function ThemePicker({ accent, onAccentChange, bodyColor, onBodyColorChange }) {
  const [showCustom, setShowCustom] = useState(false);
  const [activeGroup, setActiveGroup] = useState("Professional");
  const [customVal, setCustomVal] = useState(accent);
  const currentTheme = ALL_THEMES.find(t=>t.primary===accent);
  const groupThemes  = THEME_GROUPS.find(g=>g.label===activeGroup)?.themes||[];
  return (
    <div className="tp-panel">
      <div className="tp-header"><span className="tp-title">🎨 Theme & Colour</span>{currentTheme && <span className="tp-current-badge" style={{ background:accent+"18", color:accent, borderColor:accent+"40" }}>{currentTheme.label}</span>}</div>
      <div className="tp-active-strip" style={{ background:`linear-gradient(135deg, ${accent}, ${accent}bb)` }}><span className="tp-active-hex">{accent}</span><span className="tp-active-name">{currentTheme?.label||"Custom"}</span></div>
      <div className="tp-group-tabs">{THEME_GROUPS.map(g=><button key={g.label} className={`tp-group-tab${activeGroup===g.label?" active":""}`} onClick={()=>setActiveGroup(g.label)}>{g.label}</button>)}</div>
      <div className="tp-swatches">{groupThemes.map(t=><button key={t.id} className={`tp-swatch${accent===t.primary?" active":""}`} onClick={()=>{ onAccentChange(t.primary); setShowCustom(false); }}><span className="tp-swatch-circle" style={{ background:t.primary }}/><span className="tp-swatch-label">{t.label}</span>{accent===t.primary&&<span className="tp-swatch-check">✓</span>}</button>)}</div>
      <div className="tp-custom-row">
        <button className={`tp-custom-btn${showCustom?" active":""}`} onClick={()=>setShowCustom(v=>!v)}><span className="tp-custom-icon"/>Custom Colour</button>
        {showCustom && (
          <div className="tp-custom-picker">
            <div className="tp-custom-picker-row">
              <input type="color" value={customVal} className="tp-color-input" onChange={e=>{ setCustomVal(e.target.value); onAccentChange(e.target.value); }}/>
              <div className="tp-custom-preview" style={{ background:customVal }}/>
              <input type="text" value={customVal} maxLength={7} className="tp-hex-input" placeholder="#000000" onChange={e=>{ const v=e.target.value; setCustomVal(v); if (/^#[0-9a-fA-F]{6}$/.test(v)) onAccentChange(v); }}/>
            </div>
            <button className="tp-apply-btn" style={{ background:customVal }} onClick={()=>{ onAccentChange(customVal); setShowCustom(false); }}>Apply This Colour</button>
          </div>
        )}
      </div>
      <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid #f0e6d8" }}>
        <div style={{ fontSize:"0.72rem", fontWeight:700, color:"#7a6355", marginBottom:8 }}>📄 Background Colour</div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {BODY_COLORS.map(({ label, bg }) => (
            <button key={bg} title={label} onClick={()=>onBodyColorChange(bg)} style={{ width:24, height:24, borderRadius:6, background:bg, padding:0, border: bodyColor===bg ? "2.5px solid #1a1a1a" : "1.5px solid #d4b896", cursor:"pointer", boxShadow:"0 1px 3px rgba(0,0,0,0.1)", transform: bodyColor===bg ? "scale(1.15)" : "scale(1)", transition:"all 0.12s" }}/>
          ))}
          <input type="color" value={bodyColor||"#ffffff"} title="Custom background" onChange={e=>onBodyColorChange(e.target.value)} style={{ width:24, height:24, border:"1.5px solid #d4b896", borderRadius:6, padding:1, cursor:"pointer" }}/>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   ATS PANEL
═══════════════════════════════════════════════════════════ */
function ATSPanel({ data }) {
  const { score, reasons } = useMemo(()=>calcATS(data),[data]);
  const color = score>=80?"#263012":score>=60?"#4a5e20":"#394420";
  const label = score>=80?"Excellent":score>=60?"Good":score>=40?"Fair":"Needs Work";
  return (
    <div className="ats-panel">
      <div className="ats-header"><span className="ats-title">📊 ATS Score</span><span className="ats-label" style={{ color, background:color+"18" }}>{label}</span></div>
      <div className="ats-ring-wrap">
        <svg width="90" height="90" viewBox="0 0 90 90">
          <circle cx="45" cy="45" r="37" fill="none" stroke="#f1f5f9" strokeWidth="8"/>
          <circle cx="45" cy="45" r="37" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" strokeDasharray={`${(score/100)*232} 232`} style={{ transform:"rotate(-90deg)", transformOrigin:"45px 45px", transition:"stroke-dasharray 1s ease" }}/>
        </svg>
        <div className="ats-score-center"><div className="ats-score-num">{score}</div><div className="ats-score-max">/100</div></div>
      </div>
      {reasons.length>0&&<div><div className="ats-tips-title">Improve your score:</div>{reasons.map((r,i)=><div key={i} className="ats-tip"><span>○</span>{r}</div>)}</div>}
      {score>=80&&<div className="ats-great">🎉 Great ATS score! Your resume will pass most filters.</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   FIELD WITH AI SUGGESTION (Manual Builder)
═══════════════════════════════════════════════════════════ */
function AIField({ label, fieldKey, value, onChange, placeholder, multiline=false, rows=3, required=false }) {
  const [error, setError]         = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [loadingSug, setLoadingSug] = useState(false);
  const [touched, setTouched]     = useState(false);
  const debounceRef = useRef(null);

  function handleChange(v) {
    onChange(v);
    if (touched) setError(validateField(fieldKey, v));
    // Debounce AI suggestion for relevant fields
    if (["summary","experience","skills","title"].includes(fieldKey)) {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async ()=>{
        if (v.trim().length>20) {
          setLoadingSug(true);
          const sug = await getAISuggestion(fieldKey, v);
          setSuggestion(sug);
          setLoadingSug(false);
        }
      }, 2000);
    }
  }

  function handleBlur() {
    setTouched(true);
    setError(validateField(fieldKey, value));
  }

  const inputProps = {
    value: value||"",
    onChange: e=>handleChange(e.target.value),
    onBlur: handleBlur,
    placeholder,
    style: { width:"100%", border:`1.5px solid ${error?"#394420":"#e5e7eb"}`, borderRadius:8, padding:"8px 12px", fontSize:"0.85rem", outline:"none", fontFamily:"inherit", boxSizing:"border-box", transition:"border-color 0.2s", background:"#fafafa" }
  };

  return (
    <div style={{ marginBottom:4 }}>
      <label style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>
        {label}{required&&<span style={{ color:"#394420" }}> *</span>}
      </label>
      {multiline
        ? <textarea {...inputProps} rows={rows} style={{ ...inputProps.style, resize:"vertical" }}/>
        : <input {...inputProps}/>
      }
      {error && <div style={{ fontSize:"0.71rem", color:"#394420", marginTop:3, display:"flex", alignItems:"center", gap:4 }}>⚠️ {error}</div>}
      {loadingSug && <div style={{ fontSize:"0.7rem", color:"#6366f1", marginTop:4, display:"flex", alignItems:"center", gap:5 }}><span style={{ animation:"spin 1s linear infinite", display:"inline-block" }}>⟳</span> AI is generating a suggestion...</div>}
      {suggestion && !loadingSug && (
        <div style={{ marginTop:6, background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:8, padding:"8px 10px" }}>
          <div style={{ fontSize:"0.68rem", fontWeight:800, color:"#0369a1", marginBottom:4 }}>🤖 AI Suggestion:</div>
          <div style={{ fontSize:"0.75rem", color:"#374151", lineHeight:1.5, marginBottom:6 }}>{suggestion}</div>
          <button onClick={()=>{ onChange(suggestion); setSuggestion(null); }} style={{ fontSize:"0.7rem", fontWeight:700, background:"#0ea5e9", color:"#fff", border:"none", borderRadius:5, padding:"4px 10px", cursor:"pointer", marginRight:6 }}>Use This ✓</button>
          <button onClick={()=>setSuggestion(null)} style={{ fontSize:"0.7rem", color:"#64748b", background:"none", border:"none", cursor:"pointer" }}>Dismiss</button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SKILL ADDER
═══════════════════════════════════════════════════════════ */
function SkillAdder({ onAdd }) {
  const [v,setV]=useState("");
  return (
    <div className="rb-skill-adder">
      <input value={v} onChange={e=>setV(e.target.value)} onKeyDown={e=>e.key==="Enter"&&v.trim()&&(onAdd(v.trim()),setV(""))} placeholder="Type a skill and press Enter"/>
      <button className="rb-skill-add-btn" onClick={()=>v.trim()&&(onAdd(v.trim()),setV(""))}>+ Add</button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MANUAL BUILDER FORM (with validators + AI suggestions)
═══════════════════════════════════════════════════════════ */
function ManualBuilder({ data, onChange }) {
  const set     = (f,v) => onChange({ ...data, [f]:v });
  const addExp  = () => onChange({ ...data, experience:[...(data.experience||[]),{ id:uid(), company:"", role:"", duration:"", bullets:[] }] });
  const updExp  = (id,f,v) => onChange({ ...data, experience:data.experience.map(e=>e.id===id?{ ...e,[f]:v }:e) });
  const remExp  = id => onChange({ ...data, experience:data.experience.filter(e=>e.id!==id) });
  const addBullet = id => onChange({ ...data, experience:data.experience.map(e=>e.id===id?{ ...e, bullets:[...(e.bullets||[]),""] }:e) });
  const updBullet = (eid,bi,v) => onChange({ ...data, experience:data.experience.map(e=>e.id===eid?{ ...e, bullets:e.bullets.map((b,i)=>i===bi?v:b) }:e) });
  const remBullet = (eid,bi) => onChange({ ...data, experience:data.experience.map(e=>e.id===eid?{ ...e, bullets:e.bullets.filter((_,i)=>i!==bi) }:e) });
  const addEdu  = () => onChange({ ...data, education:[...(data.education||[]),{ id:uid(), degree:"", institution:"", year:"", gpa:"" }] });
  const updEdu  = (id,f,v) => onChange({ ...data, education:data.education.map(e=>e.id===id?{ ...e,[f]:v }:e) });
  const remEdu  = id => onChange({ ...data, education:data.education.filter(e=>e.id!==id) });
  const addCert = () => onChange({ ...data, certifications:[...(data.certifications||[]),{ id:uid(), name:"", issuer:"", year:"" }] });
  const updCert = (id,f,v) => onChange({ ...data, certifications:data.certifications.map(c=>c.id===id?{ ...c,[f]:v }:c) });
  const remCert = id => onChange({ ...data, certifications:data.certifications.filter(c=>c.id!==id) });
  const addProj = () => onChange({ ...data, projects:[...(data.projects||[]),{ id:uid(), name:"", description:"", tech:"", link:"" }] });
  const updProj = (id,f,v) => onChange({ ...data, projects:data.projects.map(p=>p.id===id?{ ...p,[f]:v }:p) });
  const remProj = id => onChange({ ...data, projects:data.projects.filter(p=>p.id!==id) });

  return (
    <div className="rb-form">
      <div className="rb-form-sec">
        <div className="rb-form-sec-title">Personal Information</div>
        <div className="rb-form-grid">
          <div className="rb-field"><AIField label="Full Name" fieldKey="name" value={data.name} onChange={v=>set("name",v)} placeholder="Priya Sharma" required/></div>
          <div className="rb-field"><AIField label="Professional Title" fieldKey="title" value={data.title} onChange={v=>set("title",v)} placeholder="Software Engineer" required/></div>
          <div className="rb-field"><AIField label="Email" fieldKey="email" value={data.email} onChange={v=>set("email",v)} placeholder="priya@email.com" required/></div>
          <div className="rb-field"><AIField label="Phone" fieldKey="phone" value={data.phone} onChange={v=>set("phone",v)} placeholder="+91 98765 43210" required/></div>
          <div className="rb-field"><AIField label="Location" fieldKey="location" value={data.location} onChange={v=>set("location",v)} placeholder="Bangalore, Karnataka"/></div>
          <div className="rb-field rb-field--full"><AIField label="LinkedIn" fieldKey="linkedin" value={data.linkedin} onChange={v=>set("linkedin",v)} placeholder="linkedin.com/in/priya"/></div>
          <div className="rb-field rb-field--full">
            <label style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>Website / Portfolio</label>
            <input value={data.website||""} onChange={e=>set("website",e.target.value)} placeholder="priya.dev" style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:8, padding:"8px 12px", fontSize:"0.85rem", outline:"none", boxSizing:"border-box" }}/>
          </div>
        </div>
      </div>

      <div className="rb-form-sec">
        <div className="rb-form-sec-title">Professional Summary</div>
        <AIField label="Summary" fieldKey="summary" value={data.summary} onChange={v=>set("summary",v)} placeholder="Write a 2-3 sentence summary highlighting your experience, key skills, and career goals…" multiline rows={4}/>
        <div className="rb-char-hint">{data.summary?.length||0} chars · Aim for 100–250</div>
      </div>

      <div className="rb-form-sec">
        <div className="rb-form-sec-hdr"><div className="rb-form-sec-title">Work Experience</div><button className="rb-add-btn" onClick={addExp}>+ Add Position</button></div>
        {(data.experience||[]).map(exp=>(
          <div key={exp.id} className="rb-card-form">
            <button className="rb-remove-btn" onClick={()=>remExp(exp.id)}>✕</button>
            <div className="rb-form-grid">
              <div className="rb-field"><label style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>Company *</label><input value={exp.company} onChange={e=>updExp(exp.id,"company",e.target.value)} placeholder="Google India" style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:8, padding:"8px 12px", fontSize:"0.85rem", outline:"none", boxSizing:"border-box" }}/></div>
              <div className="rb-field"><label style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>Role *</label><input value={exp.role} onChange={e=>updExp(exp.id,"role",e.target.value)} placeholder="Senior Developer" style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:8, padding:"8px 12px", fontSize:"0.85rem", outline:"none", boxSizing:"border-box" }}/></div>
              <div className="rb-field rb-field--full"><label style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>Duration</label><input value={exp.duration} onChange={e=>updExp(exp.id,"duration",e.target.value)} placeholder="Jan 2022 – Present" style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:8, padding:"8px 12px", fontSize:"0.85rem", outline:"none", boxSizing:"border-box" }}/></div>
            </div>
            <div className="rb-bullets-sec">
              <label style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151" }}>Key Achievements</label>
              {(exp.bullets||[]).map((b,bi)=>(
                <div key={bi} className="rb-bullet-row">
                  <span className="rb-bullet-dot">▸</span>
                  <input value={b} onChange={e=>updBullet(exp.id,bi,e.target.value)} placeholder="Describe an achievement with numbers…" style={{ flex:1, border:"1.5px solid #e5e7eb", borderRadius:8, padding:"7px 10px", fontSize:"0.82rem", outline:"none" }}/>
                  <button className="rb-bullet-rem" onClick={()=>remBullet(exp.id,bi)}>✕</button>
                </div>
              ))}
              <button className="rb-add-bullet-btn" onClick={()=>addBullet(exp.id)}>+ Add point</button>
            </div>
          </div>
        ))}
      </div>

      <div className="rb-form-sec">
        <div className="rb-form-sec-hdr"><div className="rb-form-sec-title">Education</div><button className="rb-add-btn" onClick={addEdu}>+ Add</button></div>
        {(data.education||[]).map(edu=>(
          <div key={edu.id} className="rb-card-form">
            <button className="rb-remove-btn" onClick={()=>remEdu(edu.id)}>✕</button>
            <div className="rb-form-grid">
              <div className="rb-field"><label style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>Degree *</label><input value={edu.degree} onChange={e=>updEdu(edu.id,"degree",e.target.value)} placeholder="B.Tech Computer Science" style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:8, padding:"8px 12px", fontSize:"0.85rem", outline:"none", boxSizing:"border-box" }}/></div>
              <div className="rb-field"><label style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>Institution *</label><input value={edu.institution} onChange={e=>updEdu(edu.id,"institution",e.target.value)} placeholder="IIT Bombay" style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:8, padding:"8px 12px", fontSize:"0.85rem", outline:"none", boxSizing:"border-box" }}/></div>
              <div className="rb-field"><label style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>Year</label><input value={edu.year} onChange={e=>updEdu(edu.id,"year",e.target.value)} placeholder="2020" style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:8, padding:"8px 12px", fontSize:"0.85rem", outline:"none", boxSizing:"border-box" }}/></div>
              <div className="rb-field"><label style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>GPA / %</label><input value={edu.gpa} onChange={e=>updEdu(edu.id,"gpa",e.target.value)} placeholder="8.5 CGPA" style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:8, padding:"8px 12px", fontSize:"0.85rem", outline:"none", boxSizing:"border-box" }}/></div>
            </div>
          </div>
        ))}
      </div>

      <div className="rb-form-sec">
        <div className="rb-form-sec-title">Skills</div>
        <div className="rb-skills-tags">{(data.skills||[]).map((s,i)=><span key={i} className="rb-skill-pill">{s}<button onClick={()=>set("skills",data.skills.filter((_,j)=>j!==i))}>✕</button></span>)}</div>
        <SkillAdder onAdd={s=>set("skills",[...(data.skills||[]),s])}/>
        <div className="rb-skills-hint">Add 8–12 skills for best ATS performance.</div>
      </div>

      <div className="rb-form-sec">
        <div className="rb-form-sec-hdr"><div className="rb-form-sec-title">Certifications</div><button className="rb-add-btn" onClick={addCert}>+ Add</button></div>
        {(data.certifications||[]).map(cert=>(
          <div key={cert.id} className="rb-card-form">
            <button className="rb-remove-btn" onClick={()=>remCert(cert.id)}>✕</button>
            <div className="rb-form-grid">
              <div className="rb-field rb-field--full"><label style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>Certification Name</label><input value={cert.name} onChange={e=>updCert(cert.id,"name",e.target.value)} placeholder="AWS Certified Developer" style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:8, padding:"8px 12px", fontSize:"0.85rem", outline:"none", boxSizing:"border-box" }}/></div>
              <div className="rb-field"><label style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>Issuing Body</label><input value={cert.issuer} onChange={e=>updCert(cert.id,"issuer",e.target.value)} placeholder="Amazon" style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:8, padding:"8px 12px", fontSize:"0.85rem", outline:"none", boxSizing:"border-box" }}/></div>
              <div className="rb-field"><label style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>Year</label><input value={cert.year} onChange={e=>updCert(cert.id,"year",e.target.value)} placeholder="2023" style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:8, padding:"8px 12px", fontSize:"0.85rem", outline:"none", boxSizing:"border-box" }}/></div>
            </div>
          </div>
        ))}
      </div>

      <div className="rb-form-sec">
        <div className="rb-form-sec-hdr"><div className="rb-form-sec-title">Projects</div><button className="rb-add-btn" onClick={addProj}>+ Add</button></div>
        {(data.projects||[]).map(proj=>(
          <div key={proj.id} className="rb-card-form">
            <button className="rb-remove-btn" onClick={()=>remProj(proj.id)}>✕</button>
            <div className="rb-form-grid">
              <div className="rb-field"><label style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>Project Name</label><input value={proj.name} onChange={e=>updProj(proj.id,"name",e.target.value)} placeholder="E-Commerce Platform" style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:8, padding:"8px 12px", fontSize:"0.85rem", outline:"none", boxSizing:"border-box" }}/></div>
              <div className="rb-field"><label style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>Tech Stack</label><input value={proj.tech} onChange={e=>updProj(proj.id,"tech",e.target.value)} placeholder="React, Node.js" style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:8, padding:"8px 12px", fontSize:"0.85rem", outline:"none", boxSizing:"border-box" }}/></div>
              <div className="rb-field rb-field--full"><label style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>Description</label><textarea rows={2} value={proj.description} onChange={e=>updProj(proj.id,"description",e.target.value)} placeholder="Your role, impact, and outcome…" style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:8, padding:"8px 12px", fontSize:"0.85rem", outline:"none", boxSizing:"border-box", resize:"vertical" }}/></div>
              <div className="rb-field rb-field--full"><label style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151", display:"block", marginBottom:4 }}>Project Link</label><input value={proj.link} onChange={e=>updProj(proj.id,"link",e.target.value)} placeholder="github.com/priya/project" style={{ width:"100%", border:"1.5px solid #e5e7eb", borderRadius:8, padding:"8px 12px", fontSize:"0.85rem", outline:"none", boxSizing:"border-box" }}/></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   RESUME TEMPLATE MARK
═══════════════════════════════════════════════════════════ */
function TalentHiveMark({ accent="#2563eb" }) {
  return (
    <div className="rb-mark">
      <span className="rb-mark-dot" style={{ background:accent+"55" }}/>
      Built with <strong style={{ color:accent }}>TalentHive</strong> · talentive.in
      <span className="rb-mark-dot" style={{ background:accent+"55" }}/>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SIX RESUME TEMPLATES
═══════════════════════════════════════════════════════════ */
function ModernTemplate({ data, accent, bodyColor="#ffffff" }) {
  const acc = accent||"#2563eb";
  return (
    <div style={{ width:"100%", minHeight:1123, fontFamily:"'Lato',sans-serif", background:bodyColor, display:"flex" }}>
      <div style={{ width:230, minWidth:230, background:acc, color:"#fff", padding:"28px 20px", display:"flex", flexDirection:"column" }}>
        <div style={{ display:"flex", justifyContent:"center", marginBottom:18 }}>
          {data.photo ? <img src={data.photo} alt="" style={{ width:90, height:90, borderRadius:"50%", objectFit:"cover", border:"3px solid rgba(255,255,255,.5)" }}/> : <div style={{ width:90, height:90, borderRadius:"50%", background:"rgba(255,255,255,.15)", border:"2px dashed rgba(255,255,255,.4)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"2.2rem" }}>👤</div>}
        </div>
        <div style={{ fontSize:".96rem", fontWeight:900, textAlign:"center", marginBottom:4 }}>{data.name||"Your Name"}</div>
        <div style={{ fontSize:".7rem", textAlign:"center", opacity:.82, marginBottom:16 }}>{data.title}</div>
        <div style={{ height:1, background:"rgba(255,255,255,.2)", margin:"4px 0 16px" }}/>
        <div style={{ marginBottom:18 }}>
          <div style={{ fontSize:".6rem", fontWeight:900, letterSpacing:".12em", textTransform:"uppercase", opacity:.65, marginBottom:8 }}>CONTACT</div>
          {data.email    && <div style={{ fontSize:".67rem", opacity:.9, padding:"3px 0", wordBreak:"break-all" }}>✉ {data.email}</div>}
          {data.phone    && <div style={{ fontSize:".67rem", opacity:.9, padding:"3px 0" }}>📞 {data.phone}</div>}
          {data.location && <div style={{ fontSize:".67rem", opacity:.9, padding:"3px 0" }}>📍 {data.location}</div>}
          {data.linkedin && <div style={{ fontSize:".67rem", opacity:.9, padding:"3px 0", wordBreak:"break-all" }}>in {data.linkedin}</div>}
        </div>
        {data.skills?.length>0 && (
          <div style={{ marginBottom:18 }}>
            <div style={{ fontSize:".6rem", fontWeight:900, letterSpacing:".12em", textTransform:"uppercase", opacity:.65, marginBottom:8 }}>SKILLS</div>
            {data.skills.map((s,i)=>(
              <div key={i} style={{ marginBottom:7, fontSize:".67rem" }}>
                <span>{s}</span>
                <div style={{ height:3, background:"rgba(255,255,255,.2)", borderRadius:2, marginTop:2 }}><div style={{ height:"100%", background:"rgba(255,255,255,.75)", borderRadius:2, width:`${Math.min(90,60+i*7)}%` }}/></div>
              </div>
            ))}
          </div>
        )}
        {data.certifications?.length>0 && (
          <div>
            <div style={{ fontSize:".6rem", fontWeight:900, letterSpacing:".12em", textTransform:"uppercase", opacity:.65, marginBottom:8 }}>CERTIFICATIONS</div>
            {data.certifications.map((c,i)=><div key={i} style={{ fontSize:".67rem", opacity:.9, padding:"3px 0" }}>🏅 {c.name}</div>)}
          </div>
        )}
      </div>
      <div style={{ flex:1, padding:"28px 26px 20px" }}>
        {data.summary && <div style={{ marginBottom:18 }}><div style={{ fontSize:".63rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".12em", color:acc, borderBottom:`2px solid ${acc}`, paddingBottom:4, marginBottom:10 }}>PROFESSIONAL SUMMARY</div><p style={{ fontSize:".82rem", color:"#374151", lineHeight:1.7 }}>{data.summary}</p></div>}
        {data.experience?.length>0 && <div style={{ marginBottom:18 }}><div style={{ fontSize:".63rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".12em", color:acc, borderBottom:`2px solid ${acc}`, paddingBottom:4, marginBottom:10 }}>WORK EXPERIENCE</div>
          {data.experience.map((e,i)=>(
            <div key={i} style={{ display:"flex", gap:10, marginBottom:14 }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background:acc, marginTop:5, flexShrink:0 }}/>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", justifyContent:"space-between", flexWrap:"wrap" }}><strong style={{ fontSize:".86rem" }}>{e.role}</strong><span style={{ fontSize:".72rem", color:acc, fontWeight:700 }}>{e.duration}</span></div>
                <div style={{ fontSize:".75rem", color:"#6b7280", marginBottom:5 }}>{e.company}</div>
                {e.bullets?.length>0 && <ul style={{ paddingLeft:16 }}>{e.bullets.map((b,j)=><li key={j} style={{ fontSize:".75rem", color:"#374151", lineHeight:1.6 }}>{b}</li>)}</ul>}
              </div>
            </div>
          ))}
        </div>}
        {data.education?.length>0 && <div style={{ marginBottom:18 }}><div style={{ fontSize:".63rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".12em", color:acc, borderBottom:`2px solid ${acc}`, paddingBottom:4, marginBottom:10 }}>EDUCATION</div>{data.education.map((e,i)=><div key={i} style={{ marginBottom:9, fontSize:".82rem", color:"#374151" }}><strong>{e.degree}</strong><div>{e.institution}{e.year&&<span style={{ color:"#9ca3af" }}> · {e.year}</span>}{e.gpa&&<span style={{ color:"#9ca3af" }}> · {e.gpa}</span>}</div></div>)}</div>}
        {data.projects?.length>0 && <div style={{ marginBottom:18 }}><div style={{ fontSize:".63rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".12em", color:acc, borderBottom:`2px solid ${acc}`, paddingBottom:4, marginBottom:10 }}>PROJECTS</div>{data.projects.map((p,i)=><div key={i} style={{ fontSize:".77rem", color:"#374151", padding:"4px 0" }}>{p.name&&<strong>{p.name}: </strong>}{p.description}{p.tech&&<span style={{ color:acc }}> [{p.tech}]</span>}</div>)}</div>}
        <TalentHiveMark accent={acc}/>
      </div>
    </div>
  );
}

function MinimalTemplate({ data, accent, bodyColor="#ffffff" }) {
  const acc = accent||"#1e3a5f";
  return (
    <div style={{ width:"100%", minHeight:1123, fontFamily:"'Lato',sans-serif", background:bodyColor }}>
      <div style={{ height:6, background:acc }}/>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", padding:"28px 40px 16px", gap:20 }}>
        <div style={{ flex:1 }}>
          <h1 style={{ fontFamily:"'Lato',sans-serif", fontSize:"1.8rem", fontWeight:900, color:acc, marginBottom:4 }}>{data.name||"Your Name"}</h1>
          <div style={{ fontSize:".9rem", color:"#6b7280", marginBottom:9 }}>{data.title}</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:"6px 16px", fontSize:".72rem", color:"#9ca3af" }}>
            {data.email && <span>✉ {data.email}</span>}{data.phone && <span>📞 {data.phone}</span>}{data.location && <span>📍 {data.location}</span>}{data.linkedin && <span>in {data.linkedin}</span>}
          </div>
        </div>
        {data.photo && <div style={{ width:84, height:84, flexShrink:0, border:`3px solid ${acc}`, borderRadius:8, overflow:"hidden" }}><img src={data.photo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/></div>}
      </div>
      <div style={{ height:2, background:acc, margin:"0 40px" }}/>
      <div style={{ padding:"18px 40px 20px" }}>
        {data.summary && <div style={{ marginBottom:18 }}><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".12em", color:acc, marginBottom:8 }}>PROFILE SUMMARY</div><p style={{ fontSize:".82rem", color:"#374151", lineHeight:1.68 }}>{data.summary}</p></div>}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 230px", gap:26 }}>
          <div>
            {data.experience?.length>0 && <div style={{ marginBottom:16 }}><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".12em", color:acc, marginBottom:10 }}>EXPERIENCE</div>{data.experience.map((e,i)=><div key={i} style={{ marginBottom:14 }}><div style={{ display:"flex", justifyContent:"space-between" }}><strong style={{ fontSize:".86rem" }}>{e.role}</strong><span style={{ fontSize:".72rem", color:"#9ca3af" }}>{e.duration}</span></div><div style={{ fontSize:".75rem", fontWeight:700, color:acc, marginBottom:4 }}>{e.company}</div>{e.bullets?.length>0&&<ul style={{ paddingLeft:16 }}>{e.bullets.map((b,j)=><li key={j} style={{ fontSize:".75rem", color:"#374151", lineHeight:1.58 }}>{b}</li>)}</ul>}</div>)}</div>}
            {data.projects?.length>0 && <div><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".12em", color:acc, marginBottom:10 }}>PROJECTS</div>{data.projects.map((p,i)=><div key={i} style={{ fontSize:".77rem", color:"#374151", padding:"3px 0" }}>{p.name&&<strong>{p.name}: </strong>}{p.description}</div>)}</div>}
          </div>
          <div>
            {data.education?.length>0 && <div style={{ marginBottom:16 }}><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".12em", color:acc, marginBottom:8 }}>EDUCATION</div>{data.education.map((e,i)=><div key={i} style={{ fontSize:".77rem", color:"#374151", marginBottom:9 }}><strong>{e.degree}</strong><div>{e.institution}</div><div style={{ color:"#9ca3af" }}>{e.year}{e.gpa&&` · ${e.gpa}`}</div></div>)}</div>}
            {data.skills?.length>0 && <div style={{ marginBottom:16 }}><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".12em", color:acc, marginBottom:8 }}>SKILLS</div><div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>{data.skills.map((s,i)=><span key={i} style={{ fontSize:".71rem", fontWeight:700, padding:"3px 9px", borderRadius:4, background:acc+"14", color:acc, border:`1px solid ${acc}40` }}>{s}</span>)}</div></div>}
            {data.certifications?.length>0 && <div><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".12em", color:acc, marginBottom:8 }}>CERTIFICATIONS</div>{data.certifications.map((c,i)=><div key={i} style={{ fontSize:".75rem", color:"#374151", padding:"2px 0" }}>▸ {c.name}</div>)}</div>}
          </div>
        </div>
      </div>
      <TalentHiveMark accent={acc}/>
    </div>
  );
}

function CorporateTemplate({ data, accent, bodyColor="#ffffff" }) {
  const acc = accent||"#1e3a5f";
  return (
    <div style={{ width:"100%", minHeight:1123, fontFamily:"'Lato',sans-serif", background:bodyColor }}>
      <div style={{ background:acc, padding:"26px 34px", color:"#fff", display:"flex", alignItems:"center", gap:20 }}>
        {data.photo && <img src={data.photo} alt="" style={{ width:82, height:82, objectFit:"cover", borderRadius:8, border:"2px solid rgba(255,255,255,.4)" }}/>}
        <div style={{ flex:1 }}>
          <h1 style={{ fontFamily:"'Lato',sans-serif", fontSize:"1.6rem", fontWeight:900, marginBottom:3 }}>{data.name||"Your Name"}</h1>
          <div style={{ fontSize:".9rem", opacity:.85, marginBottom:6 }}>{data.title}</div>
          <div style={{ display:"flex", gap:16, fontSize:".73rem", opacity:.78, flexWrap:"wrap" }}>{data.email&&<span>✉ {data.email}</span>}{data.phone&&<span>📞 {data.phone}</span>}{data.location&&<span>📍 {data.location}</span>}{data.linkedin&&<span>in {data.linkedin}</span>}</div>
        </div>
      </div>
      <div style={{ display:"flex" }}>
        <div style={{ flex:1, padding:"22px 22px", borderRight:"1px solid #e5e7eb" }}>
          {data.summary && <div style={{ marginBottom:18 }}><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".1em", borderLeft:`3px solid ${acc}`, paddingLeft:9, color:acc, marginBottom:10 }}>ABOUT ME</div><p style={{ fontSize:".82rem", color:"#374151", lineHeight:1.68 }}>{data.summary}</p></div>}
          {data.experience?.length>0 && <div style={{ marginBottom:18 }}><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".1em", borderLeft:`3px solid ${acc}`, paddingLeft:9, color:acc, marginBottom:10 }}>WORK EXPERIENCE</div>{data.experience.map((e,i)=><div key={i} style={{ display:"flex", gap:12, marginBottom:14 }}><div style={{ width:3, minHeight:32, borderRadius:2, background:acc, flexShrink:0, marginTop:4 }}/><div><div style={{ display:"flex", justifyContent:"space-between", fontSize:".86rem" }}><strong>{e.role}</strong><span style={{ color:acc, fontSize:".72rem" }}>{e.duration}</span></div><div style={{ fontSize:".75rem", color:"#64748b", marginBottom:5 }}>{e.company}</div>{e.bullets?.length>0&&<ul style={{ paddingLeft:16 }}>{e.bullets.map((b,j)=><li key={j} style={{ fontSize:".75rem", color:"#374151", lineHeight:1.58 }}>{b}</li>)}</ul>}</div></div>)}</div>}
          {data.projects?.length>0 && <div><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".1em", borderLeft:`3px solid ${acc}`, paddingLeft:9, color:acc, marginBottom:10 }}>PROJECTS</div>{data.projects.map((p,i)=><div key={i} style={{ fontSize:".77rem", color:"#374151", padding:"3px 0" }}>{p.name&&<strong>{p.name}: </strong>}{p.description}</div>)}</div>}
        </div>
        <div style={{ width:210, padding:"22px 20px", background:"#f8fafc" }}>
          {data.education?.length>0 && <div style={{ marginBottom:18 }}><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".1em", borderLeft:`3px solid ${acc}`, paddingLeft:9, color:acc, marginBottom:10 }}>EDUCATION</div>{data.education.map((e,i)=><div key={i} style={{ fontSize:".78rem", color:"#374151", marginBottom:9 }}><strong>{e.degree}</strong><div>{e.institution}</div><div style={{ color:"#9ca3af" }}>{e.year}{e.gpa&&` · ${e.gpa}`}</div></div>)}</div>}
          {data.skills?.length>0 && <div style={{ marginBottom:18 }}><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".1em", borderLeft:`3px solid ${acc}`, paddingLeft:9, color:acc, marginBottom:10 }}>SKILLS</div>{data.skills.map((s,i)=><div key={i} style={{ marginBottom:8, fontSize:".76rem", color:"#374151" }}><span>{s}</span><div style={{ height:3, background:"#e5e7eb", borderRadius:2, marginTop:2 }}><div style={{ height:"100%", background:acc, borderRadius:2, width:`${Math.min(92,65+(i*9)%30)}%` }}/></div></div>)}</div>}
          {data.certifications?.length>0 && <div><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".1em", borderLeft:`3px solid ${acc}`, paddingLeft:9, color:acc, marginBottom:10 }}>CERTIFICATIONS</div>{data.certifications.map((c,i)=><div key={i} style={{ fontSize:".74rem", color:"#374151", padding:"3px 0" }}>🏅 {c.name}</div>)}</div>}
        </div>
      </div>
      <TalentHiveMark accent={acc}/>
    </div>
  );
}

function CreativeTemplate({ data, accent, bodyColor="#ffffff" }) {
  const acc = accent||"#7c3aed";
  return (
    <div style={{ width:"100%", minHeight:1123, fontFamily:"'Lato',sans-serif", background:bodyColor, display:"flex" }}>
      <div style={{ flex:1, padding:"32px 28px 24px 34px" }}>
        <div style={{ marginBottom:24 }}>
          <h1 style={{ fontFamily:"'Lato',sans-serif", fontSize:"1.7rem", fontWeight:900, color:"#111827", marginBottom:4 }}>{data.name||"Your Name"}</h1>
          <div style={{ fontSize:".92rem", color:acc, marginBottom:8, fontWeight:700 }}>{data.title}</div>
          <div style={{ height:3, width:54, borderRadius:2, background:acc }}/>
        </div>
        {data.summary && <div style={{ marginBottom:18 }}><SectionHead acc={acc} label="PROFESSIONAL SUMMARY"/><p style={{ fontSize:".82rem", color:"#374151", lineHeight:1.68 }}>{data.summary}</p></div>}
        {data.experience?.length>0 && <div style={{ marginBottom:18 }}><SectionHead acc={acc} label="WORK EXPERIENCE"/>{data.experience.map((e,i)=><div key={i} style={{ marginBottom:14 }}><div style={{ display:"flex", justifyContent:"space-between", flexWrap:"wrap", marginBottom:2 }}><strong style={{ fontSize:".86rem" }}>{e.role}</strong><span style={{ fontSize:".7rem", fontWeight:700, padding:"2px 8px", borderRadius:4, background:acc+"18", color:acc }}>{e.duration}</span></div><div style={{ fontSize:".75rem", fontWeight:700, color:acc, marginBottom:5 }}>{e.company}</div>{e.bullets?.length>0&&<ul style={{ paddingLeft:16 }}>{e.bullets.map((b,j)=><li key={j} style={{ fontSize:".75rem", color:"#374151", lineHeight:1.58 }}>{b}</li>)}</ul>}</div>)}</div>}
        {data.projects?.length>0 && <div style={{ marginBottom:18 }}><SectionHead acc={acc} label="PROJECTS"/>{data.projects.map((p,i)=><div key={i} style={{ fontSize:".77rem", color:"#374151", padding:"3px 0" }}>{p.name&&<strong>{p.name}: </strong>}{p.description}</div>)}</div>}
        <TalentHiveMark accent={acc}/>
      </div>
      <div style={{ width:215, borderLeft:`1px solid ${acc}30`, padding:"32px 18px", display:"flex", flexDirection:"column", alignItems:"center", gap:16, background:`${acc}06` }}>
        <div style={{ width:98, height:98, borderRadius:"50%", border:`3px solid ${acc}`, overflow:"hidden", flexShrink:0 }}>{data.photo?<img src={data.photo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>:<div style={{ width:"100%", height:"100%", background:acc+"18", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"2.2rem" }}>👤</div>}</div>
        <div style={{ width:"100%", borderTop:"1px solid #e5e7eb", paddingTop:12 }}>{data.email&&<div style={{ display:"flex", gap:6, fontSize:".71rem", color:"#374151", padding:"3px 0", wordBreak:"break-all" }}><span style={{ color:acc }}>✉</span>{data.email}</div>}{data.phone&&<div style={{ display:"flex", gap:6, fontSize:".71rem", color:"#374151", padding:"3px 0" }}><span style={{ color:acc }}>📞</span>{data.phone}</div>}{data.location&&<div style={{ display:"flex", gap:6, fontSize:".71rem", color:"#374151", padding:"3px 0" }}><span style={{ color:acc }}>📍</span>{data.location}</div>}{data.linkedin&&<div style={{ display:"flex", gap:6, fontSize:".71rem", color:"#374151", padding:"3px 0", wordBreak:"break-all" }}><span style={{ color:acc }}>in</span>{data.linkedin}</div>}</div>
        {data.education?.length>0 && <div style={{ width:"100%" }}><SmallHead acc={acc} label="EDUCATION"/>{data.education.map((e,i)=><div key={i} style={{ fontSize:".73rem", color:"#374151", marginBottom:8 }}><strong>{e.degree}</strong><div>{e.institution}</div><div style={{ color:"#9ca3af" }}>{e.year}</div></div>)}</div>}
        {data.skills?.length>0 && <div style={{ width:"100%" }}><SmallHead acc={acc} label="SKILLS"/>{data.skills.map((s,i)=><div key={i} style={{ marginBottom:6, fontSize:".71rem", color:"#374151" }}><span>{s}</span><div style={{ height:3, background:"#e5e7eb", borderRadius:2, marginTop:2 }}><div style={{ height:"100%", background:acc, borderRadius:2, width:`${Math.min(90,60+(i*11)%32)}%` }}/></div></div>)}</div>}
        {data.certifications?.length>0 && <div style={{ width:"100%" }}><SmallHead acc={acc} label="CERTIFICATIONS"/>{data.certifications.map((c,i)=><div key={i} style={{ fontSize:".73rem", color:"#374151", padding:"2px 0" }}>✓ {c.name}</div>)}</div>}
      </div>
    </div>
  );
}

function GradientTemplate({ data, accent, bodyColor="#ffffff" }) {
  const acc = accent||"#db2777";
  return (
    <div style={{ width:"100%", minHeight:1123, fontFamily:"'Lato',sans-serif", background:bodyColor }}>
      <div style={{ background:`linear-gradient(135deg, ${acc}, ${acc}dd)`, padding:"26px 34px", color:"#fff", display:"flex", alignItems:"center", gap:20 }}>
        <div style={{ width:82, height:82, borderRadius:"50%", overflow:"hidden", flexShrink:0, border:"3px solid rgba(255,255,255,.55)" }}>{data.photo?<img src={data.photo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/>:<div style={{ width:"100%", height:"100%", background:"rgba(255,255,255,.15)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"2.2rem" }}>👤</div>}</div>
        <div style={{ flex:1 }}><h1 style={{ fontFamily:"'Lato',sans-serif", fontSize:"1.55rem", fontWeight:900, marginBottom:3 }}>{data.name||"Your Name"}</h1><div style={{ fontSize:".88rem", opacity:.88, marginBottom:7 }}>{data.title}</div><div style={{ display:"flex", flexWrap:"wrap", gap:"5px 14px", fontSize:".71rem", opacity:.82 }}>{data.email&&<span>✉ {data.email}</span>}{data.phone&&<span>📞 {data.phone}</span>}{data.location&&<span>📍 {data.location}</span>}</div></div>
      </div>
      <div style={{ padding:"22px 34px" }}>
        {data.summary && <div style={{ marginBottom:18 }}><GradHead acc={acc} label="ABOUT ME"/><p style={{ fontSize:".82rem", color:"#374151", lineHeight:1.68 }}>{data.summary}</p></div>}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:24 }}>
          <div>{data.experience?.length>0&&<div style={{ marginBottom:18 }}><GradHead acc={acc} label="EXPERIENCE"/>{data.experience.map((e,i)=><div key={i} style={{ display:"flex", gap:10, marginBottom:13 }}><div style={{ width:7, height:7, borderRadius:"50%", background:acc, marginTop:5, flexShrink:0 }}/><div><div style={{ fontSize:".84rem", fontWeight:700, marginBottom:1 }}>{e.role}</div><div style={{ fontSize:".73rem", color:"#6b7280", marginBottom:4 }}>{e.company}<span style={{ color:acc }}> · {e.duration}</span></div>{e.bullets?.length>0&&<ul style={{ paddingLeft:16 }}>{e.bullets.map((b,j)=><li key={j} style={{ fontSize:".74rem", color:"#374151", lineHeight:1.58 }}>{b}</li>)}</ul>}</div></div>)}</div>}{data.projects?.length>0&&<div><GradHead acc={acc} label="PROJECTS"/>{data.projects.map((p,i)=><div key={i} style={{ fontSize:".77rem", color:"#374151", padding:"3px 0" }}>{p.name&&<strong>{p.name}: </strong>}{p.description}</div>)}</div>}</div>
          <div>{data.education?.length>0&&<div style={{ marginBottom:18 }}><GradHead acc={acc} label="EDUCATION"/>{data.education.map((e,i)=><div key={i} style={{ fontSize:".77rem", color:"#374151", marginBottom:9 }}><strong>{e.degree}</strong><div>{e.institution}</div><div style={{ color:acc }}>{e.year}{e.gpa&&` · ${e.gpa}`}</div></div>)}</div>}{data.skills?.length>0&&<div style={{ marginBottom:18 }}><GradHead acc={acc} label="SKILLS"/><div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>{data.skills.map((s,i)=><span key={i} style={{ fontSize:".71rem", fontWeight:700, padding:"3px 10px", borderRadius:4, background:acc+"16", color:acc }}>{s}</span>)}</div></div>}{data.certifications?.length>0&&<div><GradHead acc={acc} label="CERTIFICATIONS"/>{data.certifications.map((c,i)=><div key={i} style={{ fontSize:".74rem", color:"#374151", padding:"2px 0" }}>🏅 {c.name}</div>)}</div>}</div>
        </div>
      </div>
      <TalentHiveMark accent={acc}/>
    </div>
  );
}

function ElegantTemplate({ data, accent, bodyColor="#ffffff" }) {
  const acc = accent||"#0f172a";
  return (
    <div style={{ width:"100%", minHeight:1123, fontFamily:"'Lato',sans-serif", background:bodyColor, position:"relative" }}>
      <div style={{ position:"absolute", left:0, top:0, bottom:0, width:5, background:acc }}/>
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", padding:"28px 36px 16px 44px", gap:20 }}>
        <div style={{ flex:1 }}><h1 style={{ fontFamily:"'Lato',sans-serif", fontSize:"1.8rem", fontWeight:900, color:acc, marginBottom:4 }}>{data.name||"Your Name"}</h1><div style={{ fontSize:".92rem", color:"#6b7280", marginBottom:8 }}>{data.title}</div><div style={{ height:2, width:64, background:acc, borderRadius:1, marginBottom:8 }}/><div style={{ display:"flex", flexWrap:"wrap", gap:"4px 14px", fontSize:".73rem", color:"#9ca3af" }}>{data.email&&<span>✉ {data.email}</span>}{data.phone&&<span>| 📞 {data.phone}</span>}{data.location&&<span>| 📍 {data.location}</span>}{data.linkedin&&<span>| in {data.linkedin}</span>}</div></div>
        {data.photo && <div style={{ width:90, height:90, flexShrink:0, overflow:"hidden", border:`3px solid ${acc}`, borderRadius:7 }}><img src={data.photo} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }}/></div>}
      </div>
      <div style={{ padding:"8px 36px 20px 44px" }}>
        {data.summary && <div style={{ marginBottom:18 }}><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".12em", color:acc, marginBottom:5 }}>PROFESSIONAL SUMMARY</div><div style={{ height:1.5, background:acc, marginBottom:10, borderRadius:1 }}/><p style={{ fontSize:".82rem", color:"#374151", lineHeight:1.68 }}>{data.summary}</p></div>}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 200px", gap:24 }}>
          <div>{data.experience?.length>0&&<div style={{ marginBottom:18 }}><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".12em", color:acc, marginBottom:5 }}>WORK EXPERIENCE</div><div style={{ height:1.5, background:acc, marginBottom:10, borderRadius:1 }}/>{data.experience.map((e,i)=><div key={i} style={{ display:"flex", gap:12, marginBottom:14 }}><div style={{ width:4, minHeight:30, borderRadius:2, background:acc, flexShrink:0, marginTop:3 }}/><div style={{ flex:1 }}><div style={{ display:"flex", justifyContent:"space-between", flexWrap:"wrap", marginBottom:2 }}><strong style={{ fontSize:".86rem" }}>{e.role}</strong><span style={{ fontSize:".72rem", color:"#9ca3af" }}>{e.duration}</span></div><div style={{ fontSize:".75rem", fontWeight:700, color:acc, marginBottom:5 }}>{e.company}</div>{e.bullets?.length>0&&<ul style={{ paddingLeft:16 }}>{e.bullets.map((b,j)=><li key={j} style={{ fontSize:".75rem", color:"#374151", lineHeight:1.58 }}>{b}</li>)}</ul>}</div></div>)}</div>}{data.projects?.length>0&&<div><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".12em", color:acc, marginBottom:5 }}>PROJECTS</div><div style={{ height:1.5, background:acc, marginBottom:10, borderRadius:1 }}/>{data.projects.map((p,i)=><div key={i} style={{ fontSize:".77rem", color:"#374151", padding:"3px 0" }}>{p.name&&<strong>{p.name}: </strong>}{p.description}</div>)}</div>}</div>
          <div>{data.education?.length>0&&<div style={{ marginBottom:16 }}><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".12em", color:acc, marginBottom:5 }}>EDUCATION</div><div style={{ height:1.5, background:acc, marginBottom:10, borderRadius:1 }}/>{data.education.map((e,i)=><div key={i} style={{ fontSize:".77rem", color:"#374151", marginBottom:9 }}><strong>{e.degree}</strong><div>{e.institution}</div><div style={{ color:"#9ca3af" }}>{e.year}{e.gpa&&` · ${e.gpa}`}</div></div>)}</div>}{data.skills?.length>0&&<div style={{ marginBottom:16 }}><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".12em", color:acc, marginBottom:5 }}>SKILLS</div><div style={{ height:1.5, background:acc, marginBottom:10, borderRadius:1 }}/>{data.skills.map((s,i)=><div key={i} style={{ display:"flex", alignItems:"center", gap:8, fontSize:".75rem", color:"#374151", marginBottom:5 }}><span style={{ width:6, height:6, borderRadius:"50%", background:acc, flexShrink:0 }}/>{s}</div>)}</div>}{data.certifications?.length>0&&<div><div style={{ fontSize:".64rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".12em", color:acc, marginBottom:5 }}>CERTIFICATIONS</div><div style={{ height:1.5, background:acc, marginBottom:10, borderRadius:1 }}/>{data.certifications.map((c,i)=><div key={i} style={{ fontSize:".73rem", color:"#374151", padding:"2px 0" }}>✓ {c.name}</div>)}</div>}</div>
        </div>
      </div>
      <TalentHiveMark accent={acc}/>
    </div>
  );
}

function SectionHead({ acc, label }) {
  return <div style={{ display:"flex", alignItems:"center", gap:7, fontSize:".63rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".12em", color:acc, marginBottom:10 }}><span style={{ width:14, height:14, borderRadius:2, background:acc, fontSize:".5rem", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center" }}>◆</span>{label}</div>;
}
function SmallHead({ acc, label }) {
  return <div style={{ fontSize:".61rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".1em", color:acc, marginBottom:8 }}>{label}</div>;
}
function GradHead({ acc, label }) {
  return <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:".63rem", fontWeight:900, textTransform:"uppercase", letterSpacing:".1em", color:acc, marginBottom:10 }}><span style={{ width:16, height:3, borderRadius:2, background:acc, display:"inline-block" }}/>{label}</div>;
}

function ResumePreview({ data, template, accent, bodyColor="#ffffff" }) {
  const props = { data, accent, bodyColor };
  if (template==="modern")    return <ModernTemplate    {...props}/>;
  if (template==="minimal")   return <MinimalTemplate   {...props}/>;
  if (template==="corporate") return <CorporateTemplate {...props}/>;
  if (template==="creative")  return <CreativeTemplate  {...props}/>;
  if (template==="gradient")  return <GradientTemplate  {...props}/>;
  if (template==="elegant")   return <ElegantTemplate   {...props}/>;
  return <ModernTemplate {...props}/>;
}

/* ═══════════════════════════════════════════════════════════
   INLINE TEMPLATE PICKER
═══════════════════════════════════════════════════════════ */
function InlineTemplatePicker({ current, accent, bodyColor, onSelect, onAccentChange }) {
  const [filter, setFilter]   = useState("all");
  const [hovered, setHovered] = useState(null);
  return (
    <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:14, padding:14, boxShadow:"0 2px 8px rgba(0,0,0,0.05)" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
        <span style={{ fontSize:13, fontWeight:900, color:"#111827" }}>Template</span>
        <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:6, background:TEMPLATES.find(t=>t.id===current)?.type==="ats"?"#dcfce7":"#ede9fe", color:TEMPLATES.find(t=>t.id===current)?.type==="ats"?"#166534":"#6d28d9" }}>{TEMPLATES.find(t=>t.id===current)?.name||"Modern Pro"}</span>
      </div>
      <div style={{ display:"flex", gap:3, marginBottom:10 }}>
        {[["all","All"],["ats","ATS"],["design","Stylish"]].map(([v,l])=>(
          <button key={v} onClick={()=>setFilter(v)} style={{ flex:1, padding:"4px", borderRadius:6, border:"1px solid", fontSize:10, fontWeight:700, cursor:"pointer", borderColor:filter===v?"#4f46e5":"#e5e7eb", background:filter===v?"#4f46e5":"#f9fafb", color:filter===v?"#fff":"#6b7280" }}>{l}</button>
        ))}
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {TEMPLATES.filter(t=>filter==="all"||t.type===filter).map(t=>(
          <div key={t.id} onMouseEnter={()=>setHovered(t.id)} onMouseLeave={()=>setHovered(null)}
            onClick={()=>{ onSelect(t.id); onAccentChange(t.accent); }}
            style={{ display:"flex", gap:10, alignItems:"center", border:`1.5px solid ${current===t.id?"#4f46e5":hovered===t.id?"#4f46e5":"#e5e7eb"}`, borderRadius:10, overflow:"hidden", cursor:"pointer", background:current===t.id?"#eef2ff":"#fff", transition:"all 0.15s", transform:hovered===t.id&&current!==t.id?"translateX(2px)":"none" }}>
            <div style={{ width:76, height:68, overflow:"hidden", flexShrink:0, background:"#f9fafb", position:"relative" }}>
              <div style={{ position:"absolute", top:0, left:0, width:794, transformOrigin:"top left", transform:`scale(${76/794})`, pointerEvents:"none" }}>
                <ResumePreview data={PREVIEW_SAMPLE} template={t.id} accent={t.accent} bodyColor="#ffffff"/>
              </div>
            </div>
            <div style={{ flex:1, padding:"6px 8px 6px 0" }}>
              <div style={{ fontSize:11, fontWeight:800, color:"#111827", marginBottom:1 }}>{t.name}</div>
              <div style={{ fontSize:10, color:"#9ca3af", lineHeight:1.3 }}>{t.desc}</div>
              <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:4 }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:t.accent }}/>
                <span style={{ fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:4, background:t.type==="ats"?"#dcfce7":"#ede9fe", color:t.type==="ats"?"#166534":"#6d28d9" }}>{t.tag}</span>
              </div>
            </div>
            {current===t.id && <div style={{ width:20, height:20, borderRadius:"50%", background:"#4f46e5", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:900, marginRight:8, flexShrink:0 }}>✓</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TEMPLATE PICKER MODAL
═══════════════════════════════════════════════════════════ */
function TemplatePicker({ current, accent, bodyColor, onSelect, onAccentChange, onBodyColorChange, onClose }) {
  const [filter, setFilter]           = useState("all");
  const [localAccent, setLocalAccent] = useState(accent);
  const [localBody, setLocalBody]     = useState(bodyColor||"#ffffff");
  function handleAccent(c) { setLocalAccent(c); onAccentChange(c); }
  function handleBody(c)   { setLocalBody(c); onBodyColorChange(c); }
  return (
    <div className="th-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="th-modal tpl-modal" style={{ maxWidth:820 }}>
        <div className="th-modal-header"><span className="th-modal-title">Choose a Template</span><button className="th-close-btn" onClick={onClose}>✕</button></div>
        <div className="tpl-modal-body">
          <div className="tpl-filter-row">{[["all","All Templates"],["ats","✅ ATS Friendly"],["design","🎨 Design"]].map(([f,l])=><button key={f} className={`tpl-filter-btn${filter===f?" active":""}`} onClick={()=>setFilter(f)}>{l}</button>)}</div>
          <ColorBar accent={localAccent} onAccentChange={handleAccent} bodyColor={localBody} onBodyColorChange={handleBody} showBody={true}/>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:16 }}>
            {TEMPLATES.filter(t=>filter==="all"||t.type===filter).map(t=>(
              <div key={t.id} style={{ border:`2px solid ${current===t.id?"#4f46e5":"#e5e7eb"}`, borderRadius:12, overflow:"hidden", cursor:"pointer", background:"#fff", transition:"all 0.2s", boxShadow:current===t.id?"0 0 0 3px rgba(79,70,229,0.15)":"0 2px 6px rgba(0,0,0,0.06)" }}
                onClick={()=>{ onSelect(t.id, localAccent); onClose(); }}
                onMouseEnter={e=>{ if(current!==t.id) e.currentTarget.style.borderColor="#94a3b8"; e.currentTarget.style.transform="translateY(-3px)"; }}
                onMouseLeave={e=>{ e.currentTarget.style.borderColor=current===t.id?"#4f46e5":"#e5e7eb"; e.currentTarget.style.transform="translateY(0)"; }}>
                <div style={{ height:200, overflow:"hidden", background:"#f8fafc", position:"relative" }}>
                  <div style={{ position:"absolute", top:0, left:0, width:794, transformOrigin:"top left", transform:`scale(${(document.querySelector('.tpl-modal')?.offsetWidth/3-32||240)/794})`, pointerEvents:"none" }}>
                    <ResumePreview data={PREVIEW_SAMPLE} template={t.id} accent={localAccent} bodyColor={localBody}/>
                  </div>
                  <span className={`tpl-card-tag tpl-card-tag--${t.type}`} style={{ position:"absolute", top:8, right:8, zIndex:2 }}>{t.tag}</span>
                  {current===t.id && <div style={{ position:"absolute", top:8, left:8, width:24, height:24, background:"#4f46e5", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:"#fff", fontWeight:900, zIndex:2 }}>✓</div>}
                </div>
                <div style={{ padding:"10px 12px 12px" }}>
                  <div style={{ fontSize:"0.84rem", fontWeight:800, color:"#111827", marginBottom:2 }}>{t.name}</div>
                  <div style={{ fontSize:"0.72rem", color:"#9ca3af" }}>{t.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SHARE MODAL
═══════════════════════════════════════════════════════════ */
function ShareModal({ onClose }) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}${window.location.pathname}`;
  return (
    <div className="th-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="th-modal share-modal">
        <div className="th-modal-header"><span className="th-modal-title">📤 Share Your Resume</span><button className="th-close-btn" onClick={onClose}>✕</button></div>
        <div style={{ padding:"0 24px 24px" }}>
          <p className="share-sub">Share your resume link with recruiters or on social media.</p>
          <div className="share-link-row">
            <input readOnly value={link} className="share-link-input"/>
            <button className="th-btn th-btn--primary" onClick={()=>navigator.clipboard.writeText(link).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false),2000); })}>{copied?"✅ Copied!":"Copy"}</button>
          </div>
          <div className="share-grid">
            {[
              { icon:"📧", label:"Email",     sub:"Send via email",    action:()=>window.open(`mailto:?subject=My Resume&body=Check: ${link}`) },
              { icon:"💼", label:"LinkedIn",  sub:"Share on LinkedIn", action:()=>window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`) },
              { icon:"💬", label:"WhatsApp",  sub:"Send on WhatsApp",  action:()=>window.open(`https://wa.me/?text=${encodeURIComponent("Check out my resume! "+link)}`) },
              { icon:"🐦", label:"Twitter/X", sub:"Tweet it",          action:()=>window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent("Built my resume! "+link)}`) },
            ].map(o=>(
              <button key={o.label} className="share-option" onClick={o.action}>
                <span className="share-option-icon">{o.icon}</span>
                <span className="share-option-label">{o.label}</span>
                <span className="share-option-sub">{o.sub}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TEMPLATE SELECTOR (Step 1 of AI builder)
═══════════════════════════════════════════════════════════ */
function TemplateSelector({ onSelect }) {
  const [selectedId, setSelectedId] = useState("modern");
  const [filter, setFilter]         = useState("all");
  const [selAccent, setSelAccent]   = useState("#263012");
  const [selBody, setSelBody]       = useState("#ffffff");
  const previewScale = 0.24;
  const selected = TEMPLATES.find(t=>t.id===selectedId)||TEMPLATES[0];
  return (
    <div className="tpl-selector-root">
      {/* <div className="tpl-selector-header"> */}
        {/* <div className="rb-logo"><div className="rb-logo-icon">✦</div><span className="rb-logo-name">TalentHive</span></div> */}
        {/* <div className="tpl-selector-subtitle">Step 1 of 2 — Pick your resume style</div> */}
      {/* </div> */}
      <div className="tpl-selector-body">
        <div className="tpl-selector-left">
          <div className="tpl-selector-heading">Choose a Template</div>
          <p className="tpl-selector-desc">Pick the style that fits your industry. You can change anytime later.</p>
          <ColorBar accent={selAccent} onAccentChange={setSelAccent} bodyColor={selBody} onBodyColorChange={setSelBody} showBody={true}/>
          <div className="tpl-filter-row" style={{ marginBottom:16 }}>{[["all","All Templates"],["ats","✅ ATS Safe"],["design","🎨 Stylish"]].map(([f,l])=><button key={f} className={`tpl-filter-btn${filter===f?" active":""}`} onClick={()=>setFilter(f)}>{l}</button>)}</div>
          <div className="tpl-selector-grid">
            {TEMPLATES.filter(t=>filter==="all"||t.type===filter).map(t=>(
              <div key={t.id} className={`tpl-selector-card${selectedId===t.id?" selected":""}`} onClick={()=>setSelectedId(t.id)}>
                <div className="tpl-selector-card-preview">
                  <div className="tpl-selector-card-preview-page" style={{ transform: `translateX(-50%) scale(${previewScale})` }}>
                    <ResumePreview data={PREVIEW_SAMPLE} template={t.id} accent={selAccent} bodyColor={selBody}/>
                  </div>
                  <span className={`tpl-selector-card-tag tpl-selector-card-tag--${t.type}`}>{t.tag}</span>
                </div>
                <div className="tpl-selector-card-body"><div className="tpl-selector-card-name">{t.name}</div><div className="tpl-selector-card-desc">{t.desc}</div></div>
                {selectedId===t.id&&<div className="tpl-selector-card-check">✓</div>}
              </div>
            ))}
          </div>
          <div className="tpl-selector-actions">
            <div className="tpl-selector-ats-badge">
              {selected.type==="ats"
                ? <span className="tpl-badge-ats">ATS Safe - Recommended for most jobs</span>
                : <span className="tpl-badge-design">Design Template - Great for creative roles</span>}
            </div>
            <button className="tpl-selector-continue-btn" style={{ background:selAccent }} onClick={()=>onSelect(selectedId, selAccent, selBody)}>
              Continue with {selected.name} ->
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   VOICE AI BUILDER  ← NEW FEATURE-RICH COMPONENT
═══════════════════════════════════════════════════════════ */
function AITextBuilder({ template, accent, bodyColor, onComplete, onBack }) {
  const [step, setStep]               = useState(0);
  const [answers, setAnswers]         = useState({});
  const [textVal, setTextVal]         = useState("");
  const [error, setError]             = useState("");
  const [cleaning, setCleaning]       = useState(false);
  const [grammarDiff, setGrammarDiff] = useState(null);
  const [inputMode, setInputMode]     = useState("text"); // "text" | "voice"
  const [language, setLanguage]       = useState("english");
  const [voiceGender, setVoiceGender] = useState("female");
  const [paused, setPaused]           = useState(false);
  const [voiceError, setVoiceError]   = useState("");
  const [interimText, setInterimText] = useState("");
  const [questionSpoken, setQuestionSpoken] = useState(false);
  const inputRef = useRef(null);
  const recRef   = useRef(null);

  const progress = Math.round((step / STEPS.length) * 100);
  const current  = STEPS[step];
  const isMulti  = ["summary","experience","projects","education"].includes(current?.key);
  const canSkip  = ["certifications","projects","linkedin"].includes(current?.key);

  /* ── Voice engine ── */
  const handleVoiceResult = useCallback((transcript)=>{
    setInterimText("");
    setTextVal(prev => prev ? prev+" "+transcript : transcript);
  },[]);
  const handleVoiceError = useCallback((msg)=>{ setVoiceError(msg); },[]);

  const { isListening, isSpeaking, voiceSupported, ttsSupported, speak, stopSpeaking, startListening, stopListening } =
    useVoiceEngine({ language, voiceGender, onResult: handleVoiceResult, onError: handleVoiceError });

  /* ── Speak question when step or inputMode changes ── */
  useEffect(()=>{
    setQuestionSpoken(false);
    setTextVal("");
    setError("");
    setGrammarDiff(null);
    setVoiceError("");
    setInterimText("");
    if (inputMode==="voice" && !paused && ttsSupported) {
      const q = current?.q?.[language] || current?.q?.english || "";
      setTimeout(()=>{ speak(q, ()=>setQuestionSpoken(true)); }, 400);
    } else {
      setQuestionSpoken(true);
      setTimeout(()=>inputRef.current?.focus(), 100);
    }
  // eslint-disable-next-line
  },[step, inputMode, language, voiceGender, paused]);

  /* ── Live transcript for voice ── */
  useEffect(()=>{
    if (!isListening) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    recRef.current = rec;
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = VOICE_LANGUAGES.find(l=>l.id===language)?.lang || "en-IN";
    rec.onresult = (e)=>{
      let interim="", final="";
      for (let i=e.resultIndex; i<e.results.length; i++) {
        if (e.results[i].isFinal) final+=e.results[i][0].transcript+" ";
        else interim+=e.results[i][0].transcript;
      }
      if (interim) setInterimText(interim);
      if (final) { setTextVal(p=>p?p+" "+final.trim():final.trim()); setInterimText(""); }
    };
    rec.onerror=(e)=>{ setVoiceError(`Mic error: ${e.error}`); };
    rec.start();
    return ()=>{ try { rec.stop(); } catch{} };
  // eslint-disable-next-line
  },[isListening]);

  const previewData = useMemo(()=>{
    const a = answers;
    return { ...EMPTY_DATA,
      name:a.name||"", title:a.title||"", email:a.email||"", phone:a.phone||"",
      location:a.location||"", linkedin:(a.linkedin&&!/skip|nahi|nahin/i.test(a.linkedin))?a.linkedin:"",
      summary:a.summary||"",
      experience: a.experience?parseExperience(a.experience):[],
      education:  a.education?parseEducation(a.education):[],
      skills:     a.skills?parseSkills(a.skills):[],
      certifications: a.certifications?parseCertifications(a.certifications):[],
      projects:   a.projects?parseProjects(a.projects):[],
    };
  },[answers]);

  function handlePause() {
    setPaused(true);
    stopSpeaking();
    stopListening();
  }
  function handleResume() {
    setPaused(false);
    setVoiceError("");
  }

  async function doSubmit() {
    const v = textVal.trim();
    if (!v && !canSkip) { setError("Please provide an answer."); return; }
    if (!v && canSkip)  { doSkip(); return; }
    if (current.key==="email" && !/\S+@\S+\.\S+/.test(v)) { setError("Please enter a valid email address."); return; }
    const fieldErr = validateField(current.key, v);
    if (fieldErr && !["certifications","projects","linkedin","experience","education"].includes(current.key)) {
      setError(fieldErr); return;
    }
    setError(""); setCleaning(true); setGrammarDiff(null);
    try {
      const { cleaned, changed } = await cleanFieldWithAI(v, current.key);
      const final = cleaned==="skip" ? "skip" : cleaned;
      setAnswers(prev=>({ ...prev, [current.key]:final }));
      if (changed && final!=="skip") {
        setGrammarDiff({ original:v, corrected:final, fieldKey:current.key });
        setTimeout(()=>setGrammarDiff(null), 7000);
      }
      doAdvance();
    } catch {
      setAnswers(prev=>({ ...prev, [current.key]:v }));
      doAdvance();
    }
    setCleaning(false);
    setTextVal("");
    setInterimText("");
  }

  function doSkip() {
    setAnswers(p=>({ ...p, [current.key]:"skip" }));
    setTextVal(""); setError(""); setInterimText("");
    doAdvance();
  }

  function doAdvance() {
    const next = step + 1;
    if (next >= STEPS.length) doBuild();
    else setStep(next);
  }

  function goToStep(i) {
    if (i < step || answers[STEPS[i]?.key] !== undefined) setStep(i);
  }

  function doBuild() {
    const a = { ...answers };
    const data = {
      ...EMPTY_DATA,
      name: a.name||"", title: a.title||"", email: a.email||"", phone: a.phone||"",
      location: a.location||"", linkedin: (a.linkedin&&!/skip|nahi|nahin/i.test(a.linkedin))?a.linkedin:"",
      summary: a.summary||"", photo: null,
      experience:     a.experience    ?parseExperience(a.experience):[],
      education:      a.education     ?parseEducation(a.education):[],
      skills:         a.skills        ?parseSkills(a.skills):[],
      certifications: a.certifications?parseCertifications(a.certifications):[],
      projects:       a.projects      ?parseProjects(a.projects):[],
      languages: [],
    };
    onComplete({ data, template, accent, bodyColor });
  }

  const qText  = current?.q?.[language] || current?.q?.english || "";
  const hint   = current?.hint?.[language] || current?.hint?.english || "";

  return (
    <div className="ai-builder-root" style={{ marginTop:"80px" }}>
      {/* ── Top Bar ── */}
      <div className="ai-builder-topbar">
        <div className="rb-logo">
          <div className="rb-logo-icon">✦</div>
          <span className="rb-logo-name">TalentHive</span>
          <span className="rb-logo-mode">AI Builder</span>
        </div>

        {/* Voice Controls */}
        <div style={{ display:"flex", alignItems:"center", gap:8, flex:1, justifyContent:"center", flexWrap:"wrap" }}>
          {/* Input Mode Toggle */}
          <div style={{ display:"flex", background:"#f1f5f9", borderRadius:20, padding:3, gap:3 }}>
            <button onClick={()=>setInputMode("text")}
              style={{ padding:"5px 12px", borderRadius:16, border:"none", cursor:"pointer", fontSize:"0.73rem", fontWeight:700,
                background:inputMode==="text"?"#fff":"transparent", color:inputMode==="text"?"#1e40af":"#64748b",
                boxShadow:inputMode==="text"?"0 1px 4px rgba(0,0,0,0.12)":"none" }}>
              ✏️ Text
            </button>
            <button onClick={()=>{ if(!voiceSupported){ setVoiceError("Voice not supported in this browser. Use Chrome."); return; } setInputMode("voice"); }}
              style={{ padding:"5px 12px", borderRadius:16, border:"none", cursor:"pointer", fontSize:"0.73rem", fontWeight:700,
                background:inputMode==="voice"?"#fff":"transparent", color:inputMode==="voice"?"#7c3aed":"#64748b",
                boxShadow:inputMode==="voice"?"0 1px 4px rgba(0,0,0,0.12)":"none" }}>
              🎙️ Voice
            </button>
          </div>

          {/* Language */}
          <select value={language} onChange={e=>setLanguage(e.target.value)}
            style={{ border:"1px solid #e2e8f0", borderRadius:8, padding:"5px 8px", fontSize:"0.73rem", cursor:"pointer", background:"#fff", fontWeight:600 }}>
            {VOICE_LANGUAGES.map(l=><option key={l.id} value={l.id}>{l.flag} {l.label}</option>)}
          </select>

          {/* Voice Gender */}
          {inputMode==="voice" && (
            <div style={{ display:"flex", background:"#f1f5f9", borderRadius:20, padding:3, gap:3 }}>
              <button onClick={()=>setVoiceGender("female")} style={{ padding:"4px 10px", borderRadius:16, border:"none", cursor:"pointer", fontSize:"0.72rem", fontWeight:700, background:voiceGender==="female"?"#fff":"transparent", color:voiceGender==="female"?"#be185d":"#64748b" }}>👩 Female</button>
              <button onClick={()=>setVoiceGender("male")} style={{ padding:"4px 10px", borderRadius:16, border:"none", cursor:"pointer", fontSize:"0.72rem", fontWeight:700, background:voiceGender==="male"?"#fff":"transparent", color:voiceGender==="male"?"#1d4ed8":"#64748b" }}>👨 Male</button>
            </div>
          )}

          {/* Pause/Resume */}
          {!paused
            ? <button onClick={handlePause} style={{ padding:"5px 12px", borderRadius:8, border:"1px solid #fbbf24", background:"#fffbeb", color:"#92400e", fontSize:"0.73rem", fontWeight:700, cursor:"pointer" }}>⏸ Pause</button>
            : <button onClick={handleResume} style={{ padding:"5px 12px", borderRadius:8, border:"1px solid #263012", background:"#f0fdf4", color:"#166534", fontSize:"0.73rem", fontWeight:700, cursor:"pointer" }}>▶ Resume</button>
          }
        </div>

        <div className="ai-builder-topbar-right">
          <div className="ai-builder-progress-wrap">
            <div className="ai-progress-track"><div className="ai-progress-fill" style={{ width:`${progress}%` }}/></div>
            <span className="ai-progress-label">{step+1}/{STEPS.length}</span>
          </div>
          <button className="ai-exit-btn" onClick={onBack}>← Back</button>
        </div>
      </div>

      {/* ── Paused Banner ── */}
      {paused && (
        <div style={{ background:"#fffbeb", border:"1px solid #fbbf24", borderRadius:10, margin:"12px 20px", padding:"14px 18px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
          <div>
            <div style={{ fontWeight:800, color:"#92400e", marginBottom:4 }}>⏸️ Session Paused</div>
            <div style={{ fontSize:"0.8rem", color:"#78350f" }}>Your progress is saved. Click Resume to continue from Question {step+1}.</div>
          </div>
          <button onClick={handleResume} style={{ background:"#4a5e20", color:"#fff", border:"none", borderRadius:8, padding:"10px 20px", fontWeight:800, cursor:"pointer", fontSize:"0.85rem" }}>▶ Resume Session</button>
        </div>
      )}

      <div className="ai-builder-layout" style={{ opacity:paused?0.5:1, pointerEvents:paused?"none":"auto" }}>
        {/* ── Left Panel ── */}
        <div className="ai-builder-left">
          {/* Step Pills */}
          <div className="ai-step-pills">
            {STEPS.map((s,i)=>(
              <div key={i} className={`ai-step-pill${i<step?" done":i===step?" active":""}`}
                title={s.key} onClick={()=>goToStep(i)}
                style={{ cursor: i<=step||answers[s.key]!==undefined?"pointer":"default" }}>
                {i<step?"✓":i+1}
              </div>
            ))}
          </div>

          {/* Voice Error */}
          {voiceError && <div style={{ background:"#fef2f2", border:"1px solid #fca5a5", borderRadius:8, padding:"10px 14px", marginBottom:12, fontSize:"0.78rem", color:"#dc2626" }}>⚠️ {voiceError} <button onClick={()=>setVoiceError("")} style={{ float:"right", background:"none", border:"none", cursor:"pointer", color:"#dc2626" }}>✕</button></div>}

          {/* Grammar Diff */}
          {grammarDiff && (
            <div className="ai-grammar-diff">
              <div className="ai-grammar-diff-header">
                <span className="ai-grammar-diff-icon">✏️</span>
                <span className="ai-grammar-diff-title">AI Fixed: {grammarDiff.fieldKey}</span>
                <button className="ai-grammar-diff-close" onClick={()=>setGrammarDiff(null)}>✕</button>
              </div>
              <div className="ai-grammar-diff-row">
                <div className="ai-grammar-diff-before"><div className="ai-grammar-diff-label">You said:</div><div className="ai-grammar-diff-text ai-grammar-diff-text--old">{grammarDiff.original}</div></div>
                <div className="ai-grammar-diff-arrow">→</div>
                <div className="ai-grammar-diff-after"><div className="ai-grammar-diff-label">AI improved:</div><div className="ai-grammar-diff-text ai-grammar-diff-text--new">{grammarDiff.corrected}</div></div>
              </div>
            </div>
          )}

          {/* Question Card */}
          <div className="ai-question-card">
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
              <div className="ai-question-num">Question {step+1} / {STEPS.length}</div>
              {inputMode==="voice" && ttsSupported && (
                <button onClick={()=>{ stopSpeaking(); speak(qText, ()=>{}); }}
                  style={{ background:"#ede9fe", border:"none", borderRadius:6, padding:"4px 9px", fontSize:"0.72rem", cursor:"pointer", color:"#7c3aed", fontWeight:700 }}>
                  {isSpeaking?"🔊 Speaking...":"🔁 Repeat"}
                </button>
              )}
            </div>
            <div className="ai-question-text">{qText}</div>
            <div className="ai-question-hint">💡 {hint}</div>
          </div>

          {/* Input Card */}
          <div className="ai-input-card">
            {/* TEXT MODE */}
            {inputMode==="text" && (
              <>
                {isMulti
                  ? <textarea ref={inputRef} className="ai-text-input" value={textVal} onChange={e=>setTextVal(e.target.value)} placeholder={hint} rows={4} onKeyDown={e=>{ if(e.key==="Enter"&&(e.ctrlKey||e.metaKey)) doSubmit(); }}/>
                  : <input ref={inputRef} className="ai-text-input" type={current?.key==="email"?"email":"text"} value={textVal} onChange={e=>setTextVal(e.target.value)} placeholder={hint} onKeyDown={e=>e.key==="Enter"&&doSubmit()}/>}
                {isMulti && <div className="ai-multiline-hint">Ctrl+Enter to submit</div>}
              </>
            )}

            {/* VOICE MODE */}
            {inputMode==="voice" && (
              <div>
                {/* Transcript area */}
                <div style={{ minHeight:80, background:"#f8fafc", border:"2px solid", borderColor:isListening?"#7c3aed":"#e2e8f0", borderRadius:12, padding:"12px 14px", fontSize:"0.88rem", color:"#374151", lineHeight:1.6, marginBottom:10, transition:"border-color 0.2s", position:"relative" }}>
                  {textVal || <span style={{ color:"#9ca3af" }}>{isListening?"🎤 Listening...":questionSpoken?"Tap the mic and speak your answer":"Waiting..."}</span>}
                  {interimText && <span style={{ color:"#9ca3af" }}> {interimText}</span>}
                  {isListening && <div style={{ position:"absolute", top:8, right:10, display:"flex", gap:3 }}>{[0,1,2].map(i=><div key={i} style={{ width:5, height:5, borderRadius:"50%", background:"#7c3aed", animation:`voicePulse 1.2s ease-in-out ${i*0.2}s infinite` }}/>)}</div>}
                </div>
                {/* Edit transcript */}
                <textarea value={textVal} onChange={e=>setTextVal(e.target.value)} rows={2} placeholder="Edit your answer if needed…" style={{ width:"100%", border:"1px solid #e2e8f0", borderRadius:8, padding:"8px 10px", fontSize:"0.82rem", outline:"none", resize:"vertical", boxSizing:"border-box", color:"#374151" }}/>
                {/* Mic Controls */}
                <div style={{ display:"flex", gap:10, justifyContent:"center", marginTop:12 }}>
                  {!isListening
                    ? <button onClick={()=>{ setVoiceError(""); startListening(); }}
                        style={{ width:72, height:72, borderRadius:"50%", background:questionSpoken?"#7c3aed":"#94a3b8", border:"none", color:"#fff", fontSize:"1.8rem", cursor:questionSpoken?"pointer":"not-allowed", boxShadow:"0 4px 18px rgba(124,58,237,0.35)", transition:"all 0.2s" }}>🎙️</button>
                    : <button onClick={()=>{ stopListening(); }}
                        style={{ width:72, height:72, borderRadius:"50%", background:"#dc2626", border:"none", color:"#fff", fontSize:"1.8rem", cursor:"pointer", boxShadow:"0 4px 18px rgba(220,38,38,0.35)", animation:"voicePulse 1s ease-in-out infinite" }}>⏹️</button>}
                  <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                    <button onClick={()=>setTextVal("")} style={{ padding:"6px 14px", borderRadius:7, border:"1px solid #e2e8f0", background:"#fff", cursor:"pointer", fontSize:"0.75rem", color:"#64748b" }}>🗑️ Clear</button>
                    <button onClick={()=>{ if(textVal.trim()) doSubmit(); }} style={{ padding:"6px 14px", borderRadius:7, border:"none", background:"#7c3aed", color:"#fff", cursor:"pointer", fontSize:"0.75rem", fontWeight:700 }}>Submit ✓</button>
                  </div>
                </div>
                <div style={{ textAlign:"center", fontSize:"0.7rem", color:"#94a3b8", marginTop:8 }}>
                  Speak in Hindi, English, or Hinglish — AI will understand & clean up
                </div>
              </div>
            )}

            {error && <div className="ai-error">⚠️ {error}</div>}
            {cleaning && (
              <div className="ai-cleaning-bar">
                <div className="ai-cleaning-dots"><div className="ai-cleaning-dot"/><div className="ai-cleaning-dot"/><div className="ai-cleaning-dot"/></div>
                AI is translating & polishing your answer...
              </div>
            )}

            {/* Footer Buttons */}
            {inputMode==="text" && (
              <div className="ai-input-footer">
                {canSkip && <button className="ai-skip-btn" onClick={doSkip} disabled={cleaning}>Skip →</button>}
                <button className="ai-submit-btn" onClick={doSubmit} disabled={(!textVal.trim()&&!canSkip)||cleaning}>
                  {cleaning?"Processing...":step===STEPS.length-1?"Build Resume ✓":"Next →"}
                </button>
              </div>
            )}
            {inputMode==="voice" && canSkip && (
              <div style={{ textAlign:"center", marginTop:8 }}>
                <button className="ai-skip-btn" onClick={doSkip} disabled={cleaning}>Skip this question →</button>
              </div>
            )}
          </div>

          <div className="ai-grammar-note">
            🤖 AI understands Hindi, English & Hinglish — fixes all grammar automatically
          </div>
        </div>

        {/* ── Right Panel: Live Preview ── */}
        <div className="ai-builder-right">
          <div className="ai-preview-header">
            <span className="ai-preview-title">Live Preview</span>
            <div className="ai-live-dot"/>
          </div>
          <div style={{ marginBottom:8, display:"flex", gap:4, flexWrap:"wrap" }}>
            {TEMPLATES.map(t=>(
              <button key={t.id} title={t.name}
                onClick={()=>{ /* template switching in preview only */ }}
                style={{ fontSize:"0.65rem", padding:"2px 7px", borderRadius:5, border:`1px solid ${template===t.id?accent:"#e2e8f0"}`, background:template===t.id?accent:"#f8fafc", color:template===t.id?"#fff":"#64748b", cursor:"pointer", fontWeight:template===t.id?700:400 }}>
                {t.name}
              </button>
            ))}
          </div>
          <div className="ai-preview-scroll">
            <div style={{ transform:"scale(0.58)", transformOrigin:"top center", marginBottom:-470 }}>
              <ResumePreview data={previewData} template={template} accent={accent} bodyColor={bodyColor}/>
            </div>
          </div>
          {/* ATS live score */}
          {(() => { const {score} = calcATS(previewData); const color=score>=80?"#263012":score>=60?"#4a5e20":"#394420"; return (
            <div style={{ marginTop:12, background:"#f8fafc", borderRadius:8, padding:"8px 12px", display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:40, height:40, borderRadius:"50%", border:`3px solid ${color}`, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:900, fontSize:"0.82rem", color }}>{score}</div>
              <div><div style={{ fontSize:"0.72rem", fontWeight:700, color:"#374151" }}>ATS Score</div><div style={{ fontSize:"0.65rem", color:"#9ca3af" }}>Keeps improving as you answer</div></div>
            </div>
          ); })()}
        </div>
      </div>

      <style>{`
        @keyframes voicePulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.18);opacity:0.7} }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CHOOSE MODE — LANDING PAGE
═══════════════════════════════════════════════════════════ */
function ChooseMode({ onSelect }) {
  return (
    <div className="choose-root">
      <div className="choose-hero">
        <div className="choose-hero-orb choose-hero-orb--a"/><div className="choose-hero-orb choose-hero-orb--b"/>
        <div className="choose-hero-inner">
          <div className="choose-badge">
            <h1 className="choose-title">Build Your Dream <span className="choose-title-accent">Resume in Minutes</span></h1>
          </div>
        </div>
      </div>
      <div className="choose-cards-wrap">
        <div className="choose-cards-grid">
          {[
            { key:"upload", icon:"📄", badge:"Fastest", badgeCls:"--green",  title:"Upload & Edit",      cta:"Upload Resume →", ctaCls:"--green",  desc:"Upload your existing resume — AI extracts all information automatically.", features:["✅ Auto-extracts all info","✅ Edit everything instantly","✅ AI-powered parsing","✅ 6 professional templates"], cls:" choose-card--upload" },
            { key:"ai",     icon:"🎙️", badge:"Popular", badgeCls:"--prime",  title:"Voice AI Builder",   cta:"Start with Voice →", ctaCls:"--prime",  desc:"Answer questions by voice or text. Speak in Hindi, English, or Hinglish!", features:["✅ Voice & Text input","✅ Hindi/Hinglish support","✅ Male & Female AI voice","✅ Auto grammar fix"], cls:"" },
            { key:"manual", icon:"✍️", badge:"Control", badgeCls:"--purple", title:"Manual Builder",     cta:"Build Manually →", ctaCls:"--purple", desc:"Fill in your details with complete control. AI suggestions in every field.", features:["✅ AI field suggestions","✅ Real-time validation","✅ ATS score checker","✅ Custom colour themes"], cls:" choose-card--manual" },
          ].map(c=>(
            <div key={c.key} className={`choose-card${c.cls}`} onClick={()=>onSelect(c.key)}>
              <div className="choose-card-top"><span className="choose-card-icon">{c.icon}</span><span className={`choose-card-badge choose-card-badge${c.badgeCls}`}>{c.badge}</span></div>
              <h3>{c.title}</h3><p>{c.desc}</p>
              <ul className="choose-features">{c.features.map((f,i)=><li key={i}>{f}</li>)}</ul>
              <div className={`choose-cta choose-cta${c.ctaCls}`}>{c.cta}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN APP
═══════════════════════════════════════════════════════════ */
export default function ResumeBuilder() {
  const [mode,          setMode]         = useState("choose");
  const [data,          setData]         = useState({ ...EMPTY_DATA });
  const [template,      setTemplate]     = useState("modern");
  const [accent,        setAccent]       = useState("#2563eb");
  const [bodyColor,     setBodyColor]    = useState("#ffffff");
  const [activeTab,     setActiveTab]    = useState("form");
  const [showTplPicker, setShowTplPicker]= useState(false);
  const [showPhotoEd,   setShowPhotoEd]  = useState(false);
  const [photoSrc,      setPhotoSrc]     = useState(null);
  const [showShare,     setShowShare]    = useState(false);
  const [showUpload,    setShowUpload]   = useState(false);
  const [saved,         setSaved]        = useState(false);
  const [downloading,   setDownloading]  = useState(false);
  const [toastMsg,      setToastMsg]     = useState("");
  const photoInputRef = useRef(null);
  const previewRef    = useRef(null);

  useEffect(()=>{
    try {
      const raw = localStorage.getItem("th_resume_v4");
      if (raw) { const { data:d,template:t,accent:a,bodyColor:bc }=JSON.parse(raw); if(d) setData(d); if(t) setTemplate(t); if(a) setAccent(a); if(bc) setBodyColor(bc); }
    } catch {}
  },[]);

  useEffect(()=>{
    const p = new URLSearchParams(window.location.search).get("resume");
    if (p && !showUpload) setShowUpload(true);
  },[]);// eslint-disable-line

  function showToast(msg) { setToastMsg(msg); setTimeout(()=>setToastMsg(""),3500); }

  function handleSelect(key) {
    if (key==="upload") { setMode("manual"); setActiveTab("form"); setShowUpload(true); }
    else if (key==="ai") { setMode("ai-step1"); }
    else { setMode("manual"); setActiveTab("form"); }
  }

  function handleTemplateChosen(tId, tAccent, tBody) {
    setTemplate(tId); setAccent(tAccent); if(tBody) setBodyColor(tBody); setMode("ai-step2");
  }

  function handleAIComplete({ data:d, template:t, accent:a, bodyColor:bc }) {
    setData(d); setTemplate(t||"modern"); if(a) setAccent(a); if(bc) setBodyColor(bc);
    setMode("manual"); setActiveTab("preview");
    showToast("✅ Resume built! You can now edit or download.");
  }

  function sanitiseParsed(parsed) {
    if (!parsed) return { ...EMPTY_DATA };
    return { ...EMPTY_DATA, ...parsed,
      skills: Array.isArray(parsed.skills)?parsed.skills:typeof parsed.skills==="string"?parsed.skills.split(/[,;]/).map(s=>s.trim()).filter(Boolean):[],
      experience:     (parsed.experience||[]).map(e=>({ id:uid(), company:"", role:"", duration:"", bullets:[], ...e, bullets:Array.isArray(e.bullets)?e.bullets:[] })),
      education:      (parsed.education||[]).map(e=>({ id:uid(), degree:"", institution:"", year:"", gpa:"", ...e })),
      certifications: (parsed.certifications||[]).map(c=>({ id:uid(), name:"", issuer:"", year:"", ...c })),
      projects:       (parsed.projects||[]).map(p=>({ id:uid(), name:"", description:"", tech:"", link:"", ...p })),
    };
  }

  function handleUploadParsed(parsed) {
    setData(sanitiseParsed(parsed));
    setShowUpload(false); setActiveTab("form");
    showToast("✅ Resume parsed! Review and edit your information.");
  }

  function handleTemplateSelect(tId, chosenAccent) {
    setTemplate(tId);
    setAccent(chosenAccent || TEMPLATES.find(t=>t.id===tId)?.accent || "#2563eb");
  }

  function handlePhotoFile(e) {
    const f=e.target.files[0]; if(!f) return;
    const r=new FileReader();
    r.onload=ev=>{ setPhotoSrc(ev.target.result); setShowPhotoEd(true); };
    r.readAsDataURL(f);
  }

  function handleSave() {
    try {
      localStorage.setItem("th_resume_v4", JSON.stringify({ data,template,accent,bodyColor }));
      setSaved(true); setTimeout(()=>setSaved(false),2500);
      showToast("✅ Resume saved to browser!");
    } catch {}
  }

  function handleDownload() {
    setDownloading(true);
    try {
      const el = previewRef.current;
      if (!el) { setActiveTab("preview"); showToast("Switched to Preview. Click Download again."); setDownloading(false); return; }
      const w = window.open("","_blank");
      w.document.write(`<html><head><title>${data.name||"Resume"}</title><link href="https://fonts.googleapis.com/css2?family=Lato:wght@400;700;900&display=swap" rel="stylesheet"><style>body{margin:0;font-family:'Lato',sans-serif}@media print{@page{margin:0;size:A4}}</style></head><body>${el.outerHTML}</body></html>`);
      w.document.close();
      setTimeout(()=>{ w.print(); w.close(); }, 800);
    } catch { showToast("Download failed. Use browser Print (Ctrl+P)."); }
    finally { setDownloading(false); }
  }

  const tplInfo = TEMPLATES.find(t=>t.id===template)||TEMPLATES[0];

  if (mode==="choose")   return <ChooseMode onSelect={handleSelect}/>;
  if (mode==="ai-step1") return <TemplateSelector onSelect={handleTemplateChosen}/>;
  if (mode==="ai-step2") return <AITextBuilder template={template} accent={accent} bodyColor={bodyColor} onComplete={handleAIComplete} onBack={()=>setMode("ai-step1")}/>;

  return (
    <div className="rb-root" style={{ marginTop:"95px" }}>
      {toastMsg && <div className="toast">{toastMsg}</div>}

      <div className="rb-topbar">
        <div className="rb-tabs">
          {[["form","✏️ Edit"],["preview","👁 Preview"],["ats","📊 ATS Score"]].map(([k,l])=>(
            <button key={k} className={`rb-tab${activeTab===k?" active":""}`} onClick={()=>setActiveTab(k)}>{l}</button>
          ))}
        </div>
        <div className="rb-topbar-right">
          <button className="rb-upload-btn" onClick={()=>setShowUpload(true)}>📄 <span className="rb-btn-text">Upload</span></button>
          <div className="rb-topbar-sep"/>
          <button className="rb-tpl-btn" onClick={()=>setShowTplPicker(true)}>
            <span className="rb-tpl-dot" style={{ background:accent }}/>
            <span className="rb-tpl-name">{tplInfo.name}</span>
            <span className={`rb-tpl-tag rb-tpl-tag--${tplInfo.type}`}>{tplInfo.tag}</span>
          </button>
          <button className="rb-save-btn" onClick={handleSave}>{saved?"✅ Saved":"💾 Save"}</button>
          <button className="rb-share-btn" onClick={()=>setShowShare(true)}>📤 Share</button>
          <div className="rb-topbar-sep"/>
          <button className="rb-dl-btn" onClick={handleDownload} disabled={downloading}>{downloading?"⚙️ …":"⬇️ PDF"}</button>
        </div>
      </div>

      <div className="rb-content">
        {activeTab==="form" && (
          <div className="rb-edit-layout">
            <div>
              <div className="rb-photo-section">
                <div className="rb-photo-preview">
                  {data.photo ? <img src={data.photo} alt="" className="rb-photo-img"/> : <div className="rb-photo-placeholder">📷</div>}
                  <button className="rb-photo-action-btn" onClick={()=>photoInputRef.current?.click()}>{data.photo?"Change":"Add Photo"}</button>
                  {data.photo && <button className="rb-photo-action-btn" onClick={()=>{ setPhotoSrc(data.photo); setShowPhotoEd(true); }}>✏️ Edit</button>}
                  <input ref={photoInputRef} type="file" accept="image/*" hidden onChange={handlePhotoFile}/>
                </div>
                <div className="rb-photo-info">
                  <p>Add a professional headshot</p>
                  <p className="rb-photo-info-sub">JPG, PNG · Recommended 400×400px</p>
                  <button className="rb-autofill-btn" onClick={()=>setShowUpload(true)}>📄 Upload resume for auto-fill</button>
                </div>
              </div>
              <ManualBuilder data={data} onChange={setData}/>
            </div>
            <div className="rb-edit-aside">
              <InlineTemplatePicker current={template} accent={accent} bodyColor={bodyColor} onSelect={handleTemplateSelect} onAccentChange={setAccent}/>
              <ThemePicker accent={accent} onAccentChange={setAccent} bodyColor={bodyColor} onBodyColorChange={setBodyColor}/>
              <ATSPanel data={data}/>
            </div>
          </div>
        )}

        {activeTab==="preview" && (
          <div className="rb-preview-layout">
            <div className="rb-preview-actions">
              <button className="th-btn th-btn--ghost" onClick={()=>setShowTplPicker(true)}>🎨 Change Template</button>
              <button className="th-btn th-btn--ghost" onClick={()=>setShowShare(true)}>📤 Share</button>
              <button className="th-btn th-btn--primary" onClick={handleDownload}>⬇️ Download PDF</button>
            </div>
            <div className="rb-preview-colour-row">
              <ColorBar accent={accent} onAccentChange={setAccent} bodyColor={bodyColor} onBodyColorChange={setBodyColor} showBody={true}/>
            </div>
            {/* All 6 template previews */}
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:"0.78rem", fontWeight:700, color:"#374151", marginBottom:8 }}>👁 All Templates Preview — click to select:</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:10 }}>
                {TEMPLATES.map(t=>(
                  <div key={t.id} onClick={()=>handleTemplateSelect(t.id, t.accent)}
                    style={{ border:`2px solid ${template===t.id?accent:"#e5e7eb"}`, borderRadius:10, overflow:"hidden", cursor:"pointer", background:"#fff", boxShadow:template===t.id?`0 0 0 3px ${accent}33`:"none", transition:"all 0.15s" }}>
                    <div style={{ height:100, overflow:"hidden", position:"relative" }}>
                      <div style={{ position:"absolute", top:0, left:0, width:794, transformOrigin:"top left", transform:`scale(${150/794})`, pointerEvents:"none" }}>
                        <ResumePreview data={data} template={t.id} accent={accent} bodyColor={bodyColor}/>
                      </div>
                    </div>
                    <div style={{ padding:"5px 8px", fontSize:"0.7rem", fontWeight:template===t.id?800:500, color:template===t.id?accent:"#64748b", textAlign:"center" }}>{template===t.id?"✓ ":""}{t.name}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rb-preview-scroll">
              <div ref={previewRef} className="rb-preview-page">
                <ResumePreview data={data} template={template} accent={accent} bodyColor={bodyColor}/>
              </div>
            </div>
          </div>
        )}

        {activeTab==="ats" && (
          <div className="ats-full">
            <h3 className="ats-full-title">Your ATS Score Report</h3>
            <ATSPanel data={data}/>
            <div className="ats-about">
              <h4>What is an ATS Score?</h4>
              <p>Applicant Tracking Systems are used by companies like Google, TCS, Infosys, and Wipro to automatically filter resumes before a human ever sees them.</p>
              <h4>How to improve:</h4>
              <ul>{["Use keywords from the job description","Use standard section titles (Experience, Education, Skills)","Add measurable achievements (increased sales by 30%)","Keep formatting simple and consistent","Add 8-12 relevant skills","Include a strong professional summary"].map((t,i)=><li key={i}>{t}</li>)}</ul>
            </div>
          </div>
        )}
      </div>

      {showUpload    && <UploadResumePanel onParsed={handleUploadParsed} onDismiss={()=>setShowUpload(false)}/>}
      {showTplPicker && <TemplatePicker current={template} accent={accent} bodyColor={bodyColor} onSelect={handleTemplateSelect} onAccentChange={setAccent} onBodyColorChange={setBodyColor} onClose={()=>setShowTplPicker(false)}/>}
      {showPhotoEd&&photoSrc && <PhotoEditor src={photoSrc} onSave={url=>{ setData(d=>({ ...d, photo:url })); setShowPhotoEd(false); setPhotoSrc(null); }} onClose={()=>setShowPhotoEd(false)}/>}
      {showShare     && <ShareModal onClose={()=>setShowShare(false)}/>}

      <style>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes voicePulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.15);opacity:0.7} }
      `}</style>
    </div>
  );
}
